// ─────────────────────────────────────────────────────────────────────────────
// file: client/src/services/index.js
// ─────────────────────────────────────────────────────────────────────────────
/**
 * @fileoverview PHARMAKON — API service objects
 *
 * One object per backend resource. All functions return Axios response promises.
 * Callers: Zustand stores + direct component calls.
 *
 * Base URL and auth header injection handled by api.client.js (Axios instance).
 */

import api from "./api.client.js";
import { downloadCsv } from "../lib/csv-export.js";

// ─────────────────────────────────────────────────────────────────────────────
// PRODUIT API
// ─────────────────────────────────────────────────────────────────────────────

export const produitApi = {
  /**
   * Paginated catalogue.
   *
   * Accepted query params (all optional; forwarded as-is to GET /produits):
   *   laboratoire_id, status, stock_level, peremption_before, therapeutic_class,
   *   product_type, availability, lot_number, fournisseur_id, importance,
   *   q (free-text), limit, offset.
   *
   * Back-compat: legacy callers can still pass `search`, `page`, etc.
   * `opts.signal` cancels a superseded request (the S1 product picker, ux/01 §3.4).
   */
  list: (params = {}, opts = {}) => api.get("/produits", { params, signal: opts.signal }),
  /** Single product with full include */
  getById: (id) => api.get(`/produits/${id}`),
  /**
   * Create new product. Refonte WP-24: `requete_id` (N-09) makes a replay after a lost answer return
   * the product already written (`rejoue: true`, HTTP 200), never a second one.
   */
  create: (body) => api.post("/produits", body),
  /** WP-24 (ux/02 §5.7) — the product a create request wrote, or 404: asked before saying a timed-out create failed. */
  parCreation: (requeteId) => api.get(`/produits/creation/${encodeURIComponent(requeteId)}`),
  /**
   * WP-24 (ux/02 §5.3) — existing rows for an S2 identification field (`champ`: dci | forme |
   * laboratoire | marque), prefix matches first.
   */
  suggestions: (champ, q, opts = {}) => api.get("/produits/suggestions", { params: { champ, q }, signal: opts.signal }),
  /** Partial update */
  update: (id, body) => api.patch(`/produits/${id}`, body),
  /** Soft-delete */
  delete: (id) => api.delete(`/produits/${id}`),

  /**
   * Alert counts + rows.
   * Response: { counts: { lotsPerimes, lotsProchesPeremption, produitsStockBas, produitsEnRupture },
   *             expired[], nearExpiry[], lowStock[], outOfStock[] }
   */
  alerts: () => api.get("/produits/alerts"),

  /** Direct fast alert counts */
  alertCounts: () => api.get("/produits/alerts/counts"),

  /**
   * CHIFA import — the server reads the Base CNAS record of `num_enr` itself and creates the
   * médicament (201), or answers the product already in the catalogue (200, `deja_au_catalogue`).
   */
  importChifa: (num_enr) => api.post("/produits/import-chifa", { num_enr }),

  /**
   * Refonte WP-109 (CP §15.7) — the Base CNAS record of a N° as the product form needs it, read-only:
   * `{ num_enr, source, cnas, nomenclature, deja_au_catalogue, proposition, origine_champs, a_completer }`.
   * 404 CNAS_INTROUVABLE. P / M (produit:create).
   */
  prefillCnas: (num_enr, opts = {}) => api.get("/produits/prefill-cnas", { params: { num_enr }, signal: opts.signal }),

  /**
   * Cross-module X XWP-13 (refonte WP-38) — the product side panel: identity, chips, summary strip,
   * period indicators and lot cards grouped FEFO. `periode` = { jours } (a preset), { debut, fin }
   * (AAAA-MM-JJ) or {} (the server default).
   * A CAISSIER receives the redacted shape (server-side).
   */
  panneau: (id, periode = {}, opts = {}) =>
    api.get(`/produits/${id}/panneau`, { params: periode, signal: opts.signal }),
  /** X XWP-13 — « Comparer les prix d'achat » (P/M): purchase events, `generiques` adds the A-12 group. */
  historiquePrixAchat: (id, { mois, generiques = false } = {}, opts = {}) =>
    api.get(`/produits/${id}/historique-prix-achat`, {
      params: { ...(mois ? { mois } : {}), generiques: generiques ? "1" : "0" },
      signal: opts.signal,
    }),
  /**
   * X XWP-37 (XD-214) — the counter price « Vendre comme secours » proposes: `{ montant, source,
   * produit_reference }` (own last purchase, then a strict générique, then the PPA); 422 when nothing
   * prices the produit (the cashier types it). C / M / P.
   */
  prixSecours: (id, opts = {}) => api.get(`/produits/${id}/prix-secours`, { signal: opts.signal }),
  /** X XWP-13 — the « Génériques » comparison (P/M); `nomenclature` = « Inclure la nomenclature ». */
  generiques: (id, { debut, fin, jours, nomenclature = false } = {}, opts = {}) =>
    api.get(`/produits/${id}/generiques`, {
      params: { ...(debut ? { debut } : {}), ...(fin ? { fin } : {}), ...(jours ? { jours } : {}), nomenclature: nomenclature ? "1" : "0" },
      signal: opts.signal,
    }),

  /**
   * Bulk-add drugs from the central nomenclature (Base Produits).
   * Body: { nums: string[] } — num_enregistrement values (1..200).
   * Idempotent (skips already-imported). Creates type_produit=MEDICAMENT rows.
   * Returns { created, skipped, errors, produits }.
   */
  fromNomenclature: (nums) =>
    api.post("/produits/from-nomenclature", { nums }),

  /**
   * Bulk-load the ENTIRE active drug nomenclature (Base Produits) into this
   * pharmacy's catalogue in one shot — no `nums` list required. Idempotent
   * (already-present num_enregistrement values are skipped). Creates
   * type_produit=MEDICAMENT rows. Returns { created, skipped }.
   */
  // Per-request timeout override (5 min): the bulk import commits the whole
  // active catalogue server-side in chunked transactions, which exceeds the
  // axios client's 15s default. Without this the UI would surface a spurious
  // timeout while the backend keeps (correctly) creating rows.
  fromNomenclatureAll: () =>
    api.post("/produits/from-nomenclature/all", undefined, { timeout: 300_000 }),

  /**
   * Autocomplete search — used by POS barcode/name input and the command
   * palette (which passes `{ signal }` so a superseded query is aborted).
   * Returns array of { id, nom_commercial, code_barre, ppa, dci, forme, ... }
   */
  search: (q, limit = 20, config = {}) =>
    api.get("/produits", { ...config, params: { search: q, limit } }),

  /**
   * POS sellable-product search (Feature batch 4). Hits the dedicated role-aware
   * endpoint: only products with an available, cashier-visible FEFO lot, sorted
   * ACTIF before BLOQUE_VISIBLE, carrying laboratoire/forme/dosage/expiry/qty/
   * emplacement. Returns { success, data: Produit[] } — empty when nothing
   * sellable matches, which lets the caller offer "Signaler une demande".
   * XWP-12 — `config` carries the Combobox AbortSignal and extra params (`type_prefere`).
   */
  posSearch: (q, limit = 15, config = {}) =>
    api.get("/produits/search", { ...config, params: { ...(config.params ?? {}), q, limit } }),

  /**
   * CHIFA lookup (bridge) — preview before import.
   * Returns { num_enr, nom_com, nom_dci, dosage, tarif_ref, ppa_plafond, pays }
   */
  chifaLookup: (num_enr) => api.get("/chifa/lookup", { params: { num_enr } }),

  /**
   * Trigger stock formula recalculation for a single product.
   * Requires criticite + valeur to be set.
   */
  recalculerStock: (id, body = {}) => api.post(`/produits/${id}/recalculer-stock`, body),

  /**
   * Feature 1 (2026-05) — admin product blocking.
   * Body: { statut_produit: 'ACTIF' | 'BLOQUE_VISIBLE' | 'BLOQUE_CACHE',
   *         motif?: string (required when non-ACTIF) }
   * RBAC: ADMIN_PHARMAKON / PROPRIETAIRE only.
   */
  block: (id, payload) => api.patch(`/produits/${id}/block`, payload),

  /**
   * CSV export — same filter params as `list`. Triggers a browser download.
   */
  exportCsv: (filters = {}) => downloadCsv("/produits/csv", filters, "produits"),
};

// ─────────────────────────────────────────────────────────────────────────────
// VignOCR + FactureOCR API — backed by the Claude API via the Node server.
// ─────────────────────────────────────────────────────────────────────────────

// Downscale a raster image client-side before upload: Claude rejects base64
// images over ~5MB and full-res phone photos waste tokens. PDFs / non-images
// pass through untouched; EXIF orientation is honored so rotated photos read
// upright. Best-effort — any failure falls back to the original blob.
async function _downscaleImage(blob, maxEdge = 2000, quality = 0.85) {
  if (!blob || typeof blob.type !== "string" || !blob.type.startsWith("image/")) return blob;
  if (typeof createImageBitmap !== "function" || typeof document === "undefined") return blob;
  try {
    const bitmap = await createImageBitmap(blob, { imageOrientation: "from-image" });
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    canvas.getContext("2d").drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();
    const out = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
    return out || blob;
  } catch {
    return blob;
  }
}

export const ocrApi = {
  /**
   * Scan a drug vignette → structured fields:
   *   { stub, mode, confidence, field_confidence,
   *     found, produit_id?, nom, dosage, dci, forme, laboratoire,
   *     num_enr_cnas, ppa, stock_lot_id?, numero_lot, date_peremption,
   *     quantite_disponible? }
   *
   * @param {object|FormData|Blob|File} input
   *   - `{ produit_id?, numero_lot? }`   — hint payload, JSON post (stub path).
   *   - `FormData` with field "image"    — real OCR upload path (server may
   *                                        echo any of the JSON hint fields).
   *   - `Blob | File`                    — wrapped into FormData automatically.
   *
   * The vignette never carries prix_achat_ht or UG — those stay manual.
   */
  scanVignette: async (input = {}) => {
    // Blob / File → downscale then wrap as multipart for the real OCR pipeline.
    if (typeof Blob !== "undefined" && input instanceof Blob) {
      const img = await _downscaleImage(input);
      const fd = new FormData();
      fd.append("image", img, "vignette.jpg");
      return api.post("/ocr/scan-vignette", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    }
    if (typeof FormData !== "undefined" && input instanceof FormData) {
      return api.post("/ocr/scan-vignette", input, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    }
    return api.post("/ocr/scan-vignette", input);
  },

  /**
   * Extract a supplier invoice (facture) image → stock-intake prefill DTO:
   *   { header: { fournisseur_nom, numero_facture_fournisseur, date_facture },
   *     details: [{ produit_id?, produit_nom, dci, num_enr_cnas, quantite,
   *                 prix_achat_ht, prix_vente_ppa, shp, numero_lot,
   *                 date_peremption, _conf, _needsReview }],
   *     warnings: [], verification: {}, possible_duplicate? }
   * Image only (Blob/File, or a FormData carrying field "image"). The server
   * resolves products + flags arithmetic/confidence issues; never auto-commits.
   */
  extractFacture: async (input) => {
    if (typeof Blob !== "undefined" && input instanceof Blob) {
      const img = await _downscaleImage(input); // PDFs pass through untouched
      const fd = new FormData();
      fd.append("image", img, img.type === "application/pdf" ? "facture.pdf" : "facture.jpg");
      return api.post("/ocr/extract-facture", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    }
    if (typeof FormData !== "undefined" && input instanceof FormData) {
      return api.post("/ocr/extract-facture", input, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    }
    throw new Error("extractFacture: fournir une image ou un PDF (File/Blob).");
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// DERMO-CONSEILLER / AIDE-AU-CONSEIL API
// Backed by /api/v1/dermo. The taxonomy GET is freely browsable; every
// value-bearing read/write sits behind the AIDE_AU_CONSEIL soft 402-preview
// gate (rejects with { success:false, code:"MODULE_NOT_SUBSCRIBED", preview:true })
// so the page renders a real-but-limited preview, never a dead page.
// ─────────────────────────────────────────────────────────────────────────────

export const dermoApi = {
  /** Frozen platform taxonomy (UNGATED). → { tree, roots } */
  getTaxonomy: () => api.get("/dermo/taxonomy"),
  /**
   * Products a manager assigned to one leaf, ordered by rang (sellable/in-stock
   * first). → { leaf, products[] }. CAISSIER cost fields stripped server-side.
   */
  leafProducts: (leafCode) =>
    api.get(`/dermo/leaves/${encodeURIComponent(leafCode)}/products`),
  /** A product's leaf assignments. → { produit_id, assignments[] } */
  listAssignments: (produitId) =>
    api.get(`/dermo/products/${produitId}/assignments`),
  /** Assign (upsert on produit_id+leaf_code). body: { leaf_code, rang?, actif?, note? } */
  assign: (produitId, body) =>
    api.post(`/dermo/products/${produitId}/assignments`, body),
  /** Patch an existing assignment. body: { rang?, actif?, note? } */
  updateAssignment: (produitId, leafCode, body) =>
    api.patch(
      `/dermo/products/${produitId}/assignments/${encodeURIComponent(leafCode)}`,
      body,
    ),
  /** Remove an assignment (204). */
  removeAssignment: (produitId, leafCode) =>
    api.delete(
      `/dermo/products/${produitId}/assignments/${encodeURIComponent(leafCode)}`,
    ),
  /**
   * Post-sale advice (D1). Products are re-derived server-side from the vente
   * (purchased products only, no PHI). → { status:'ok'|'disabled'|'error', advice, disclaimer }.
   * 429 RATE_LIMITED when the daily per-pharmacy quota is exceeded; never throws server-side.
   */
  conseil: (venteId) => api.post("/dermo/conseil", { vente_id: venteId }),
  /**
   * D4 wizard phrasing polish. payload: { node_code } | { leaf_code }.
   * → { status:'ok'|'disabled'|'error', text }. Raw-text fallback on disabled/error;
   * AI is never in the control path.
   */
  polish: (payload) => api.post("/dermo/polish", payload),
};

// ─────────────────────────────────────────────────────────────────────────────
// EMPLACEMENT API (shelf locations, AA-00) — manager-managed, caissier-readable
// ─────────────────────────────────────────────────────────────────────────────

export const emplacementApi = {
  /** Paginated list. params: { q, produit_id, magasin_id, zone, page, limit, sort_by, sort_order } (X XWP-16) */
  list: (params = {}, config = {}) => api.get("/emplacements", { ...config, params }),
  /** All shelf locations for one product */
  byProduit: (produitId) => api.get(`/emplacements/produit/${produitId}`),
  getById: (id) => api.get(`/emplacements/${id}`),
  /** body: { code (AA-00), magasin_id, produit_id? (rangement habituel), zone?, notes? } (X XD-06) */
  create: (body) => api.post("/emplacements", body),
  update: (id, body) => api.patch(`/emplacements/${id}`, body),
  remove: (id) => api.delete(`/emplacements/${id}`),

  // ── Phase 4 — Lot ↔ Emplacement allocations (additive locator layer) ────────
  // StockLot.quantite_disponible stays authoritative; these only describe WHERE
  // within the pharmacy a lot's units physically sit. Mutations gated by the new
  // emplacement:allocate action (PROPRIETAIRE/MANAGER) server-side.
  /** Allocations for one lot (+ disponible / total_alloue / non_alloue) */
  allocationsByLot: (lotId) => api.get(`/emplacements/allocations/lot/${lotId}`),
  /** Allocations stored at one emplacement */
  allocationsByEmplacement: (id) => api.get(`/emplacements/${id}/allocations`),
  /** Set/upsert an allocation. body: { stock_lot_id, emplacement_id, quantite } (0 removes) */
  setAllocation: (body) => api.post(`/emplacements/allocations`, body),
};

// ─────────────────────────────────────────────────────────────────────────────
// VENTE API
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// DEMANDE PRODUIT API (Feature batch 4 — out-of-stock demand signal)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Out-of-stock product demand signals. The cashier raises a demand from POS when
 * search returns nothing sellable; the Propriétaire/Manager triage them from the
 * Besoin page ("Produits Demandés").
 */
export const demandeProduitApi = {
  /** Cashier raises a demand: { produit_nom, produit_id?, quantite?, notes? }. */
  create: (body) => api.post("/produits/demande", body),
  /** Triage list (paginated). Params: { statut?, from?, to?, page?, limit? }. */
  list: (params = {}) => api.get("/produits/demandes", { params }),
  /** Flip status: TRAITEE (default) / IGNOREE / EN_ATTENTE (reopen). */
  traiter: (id, statut = "TRAITEE") =>
    api.patch(`/produits/demandes/${id}`, { statut }),
};

export const venteApi = {
  /** Paginated vente history */
  list: (params = {}) => api.get("/ventes", { params }),
  /** Single vente with all details */
  getById: (id) => api.get(`/ventes/${id}`),

  /** Create Vente Libre (EN_COURS) */
  createLibre: (body) => api.post("/ventes/libre", body),
  /** Create Vente CHIFA (EN_COURS) */
  createChifa: (body) => api.post("/ventes/chifa", body),

  /** Update header (remise, notes) — EN_COURS only */
  update: (id, body) => api.patch(`/ventes/${id}`, body),

  /** Transition EN_COURS → VALIDEE + FEFO stock deduction */
  // Phase 1 — optional body carries `ordonnanciers` (psychotrope dispensing
  // data). Omitted by every non-psychotrope caller → empty body, unchanged.
  validate: (id, body) => api.post(`/ventes/${id}/validate`, body),
  /** Cancel vente + optional stock restore */
  cancel: (id, motif) => api.post(`/ventes/${id}/cancel`, { motif }),
  /** Soft-delete EN_COURS vente */
  delete: (id) => api.delete(`/ventes/${id}`),

  /** Update a single detail line */
  updateDetail: (id, detailId, body) =>
    api.patch(`/ventes/${id}/details/${detailId}`, body),
  /** Soft-delete a detail line */
  removeDetail: (id, detailId) =>
    api.delete(`/ventes/${id}/details/${detailId}`),

  /** CSV export — same filter params as `list`. */
  exportCsv: (filters = {}) => downloadCsv("/ventes/csv", filters, "ventes"),

  /** POS référentiel: remise modes and ceiling (X §9, XMD-02-09) — read once per page load. */
  referentiel: () => api.get("/ventes/referentiel"),

  // ── Novera Médecin (Module 11) — the scan-to-sale bridge (additive) ──────────
  /**
   * Resolve a scanned Novera prescription token (or RX-XXXXXX code) into a priced
   * vente-draft the POS prefills from. Idempotent (resolution, not consumption);
   * the presented prescription is self-authorizing (no counter consent gate). The
   * cashier reviews/edits/confirms through the UNCHANGED validate path.
   */
  resolveOrdonnanceNovera: (token) =>
    api.post("/medecin/ordonnances-rx/pharmacie/resolve", { token }),
  /**
   * Best-effort consented dispensing echo back to the prescription after a
   * confirmed sale (flips DELIVREE/PARTIELLE, decrements a renewable, writes the
   * vente_id). Never blocks the sale — the sale is owned wholly by Pharmakon.
   */
  dispensingEchoNovera: (ordonnance_id, body = {}) =>
    api.post(`/medecin/ordonnances-rx/${ordonnance_id}/dispensing-echo`, body),

  // ── Module 11 · P3 — caissier↔DME prescription retrieval (additive) ──────────
  /**
   * Resolve the patient's LATEST live e-prescription into the SAME priced
   * vente-draft the QR resolve returns, by an ALREADY-LINKED pharmacy client's
   * Ailaji identity. Body: { patient_account_id }. RBAC medecin_pharmacie_scan:read.
   * Uniform 404 MEDECIN_QR_INVALIDE on any miss (no membership oracle).
   */
  resolvePrescriptionByPatient: (patient_account_id) =>
    api.post("/medecin/ordonnances-rx/pharmacie/resolve-by-patient", {
      patient_account_id,
    }),
  /**
   * Resolve the patient's LATEST live e-prescription into the SAME priced
   * vente-draft, for a FIRST-TIME patient, by NIN + a COMPTOIR pairing token.
   * Body: { nin, pairing_token }. RBAC medecin_pharmacie_scan:respond.
   * Harvest-proof server-side: uniform 403 CABINET_SCOPE_FORBIDDEN (unconfirmed /
   * cross-pharmacy pairing), 429 RATE_LIMITED (over budget), 404 MEDECIN_QR_INVALIDE
   * (absent/unlinked/dangling/no-live-script) — byte-identical, no oracle.
   */
  resolvePrescriptionByNin: (nin, pairing_token) =>
    api.post("/medecin/ordonnances-rx/pharmacie/resolve-by-nin", {
      nin,
      pairing_token,
    }),
};

// ─────────────────────────────────────────────────────────────────────────────
// ACHAT API
// ─────────────────────────────────────────────────────────────────────────────

export const achatApi = {
  /** Paginated achat list */
  list: (params = {}) => api.get("/achats", { params }),
  /** Single achat with all details */
  getById: (id) => api.get(`/achats/${id}`),

  /** Create BROUILLON achat */
  create: (body) => api.post("/achats", body),
  /** Update header fields — BROUILLON only */
  update: (id, body) => api.patch(`/achats/${id}`, body),
  /** Soft-delete BROUILLON achat */
  delete: (id) => api.delete(`/achats/${id}`),

  /** Add detail lines to BROUILLON achat */
  addDetails: (id, details) => api.post(`/achats/${id}/details`, { details }),
  /** Update a detail line */
  updateDetail: (id, detailId, body) =>
    api.patch(`/achats/${id}/details/${detailId}`, body),
  /** Remove a detail line */
  removeDetail: (id, detailId) =>
    api.delete(`/achats/${id}/details/${detailId}`),

  /** Transition BROUILLON → VALIDE + create StockLots */
  validate: (id) => api.post(`/achats/${id}/validate`),

  /** CSV export — same filter params as `list`. */
  exportCsv: (filters = {}) => downloadCsv("/achats/csv", filters, "achats"),
};

// ─────────────────────────────────────────────────────────────────────────────
// FOURNISSEUR API
// ─────────────────────────────────────────────────────────────────────────────

export const fournisseurApi = {
  /** Paginated list */
  list: (params = {}) => api.get("/fournisseurs", { params }),
  /**
   * Full list for pickers — no pagination (N-05, refonte WP-21).
   * Returns [{ id, nom, telephone, email, wilaya, est_connecte, connexion, a_compte_plateforme }]
   * (`connexion` CONNECTE | INVITATION_EN_ATTENTE | NON_CONNECTE — X §11.1, XD-28)
   */
  dropdown: (opts = {}) => api.get("/fournisseurs/dropdown", { signal: opts.signal }),
  /** Single fournisseur */
  getById: (id) => api.get(`/fournisseurs/${id}`),
  /**
   * Aggregate stats for detail panel and the S1 fournisseur strip.
   * Returns { total_achats, montant_total, derniere_commande, statut_dernier, solde } —
   * `solde` is the A-02 position (refonte WP-21).
   */
  stats: (id, opts = {}) => api.get(`/fournisseurs/${id}/stats`, { signal: opts.signal }),

  /** Create + link to current pharmacie */
  create: (body) => api.post("/fournisseurs", body),
  /** Partial update */
  update: (id, body) => api.patch(`/fournisseurs/${id}`, body),
  /** Soft-delete (blocked when pending achats exist) */
  delete: (id) => api.delete(`/fournisseurs/${id}`),
  /**
   * X §11.1 (XWP-28) — ask a platform fournisseur to connect, by the e-mail it signs in with.
   * 202 `{ message }`, the same whatever the address is; the fournisseur accepts on its portal.
   */
  demanderConnexion: (email) => api.post("/fournisseurs/link-by-email", { email }),

  /** CSV export — same filter params as `list`. */
  exportCsv: (filters = {}) =>
    downloadCsv("/fournisseurs/csv", filters, "fournisseurs"),
};

// ─────────────────────────────────────────────────────────────────────────────
// DASHBOARD API
// ─────────────────────────────────────────────────────────────────────────────

export const dashboardApi = {
  /**
   * Trésorerie TWP-55 — the P / M « Sessions actives »: the OUVERTE sessions with their recap (comptoir,
   * titulaire, espèces attendues, encaissé and ventes of the officine day, CA, ancienneté) and the count of the
   * sessions closed today. `no-store`; CAISSIER 403.
   */
  sessionsActives: () => api.get("/dashboard/sessions-actives"),
  /** Full KPI summary (15 aggregates + charts) */
  summary: (params = {}) => api.get("/dashboard/summary", { params }),
  /** Period comparison (current vs previous) */
  compare: (params = {}) => api.get("/dashboard/compare", { params }),
  /** Top products by revenue */
  topProduits: (params = {}) => api.get("/dashboard/top-produits", { params }),
  /** CHIFA bridge connection status */
  bridgeStatus: () => api.get("/dashboard/bridge-status"),
  /**
   * Batch-1 §2 — l'analyse multi-paramètres (nb ventes + CA par type de
   * produit / laboratoire / DCI / caissier / type de vente / période).
   * Route SŒUR de `/dashboard` : elle n'est appelée que par la surface
   * d'analyse, donc l'ouverture du tableau de bord ne la paie pas.
   */
  analytique: (params = {}) => api.get("/dashboard/analytique", { params }),
  /** Batch-1 §2 — les revenus par caissier (CA, ventes, panier moyen, période précédente). */
  caissiers: (params = {}) => api.get("/dashboard/caissiers", { params }),
  /**
   * X §5 (XWP-24) — « Activité des caissiers » : tickets par heure, Libre / CHIFA / Instances, sur une
   * période de l'officine (`periode` AUJOURDHUI…PERSONNALISEE, `debut` / `fin` pour la personnalisée),
   * `caissier_ids` (liste séparée par des virgules, vide = tous), `agregation` TOTAL | MOYENNE.
   */
  activiteCaissiers: (params = {}, opts = {}) =>
    api.get("/dashboard/caissiers/activite", { params, signal: opts.signal }),
  /**
   * X §9.1 (XWP-07 / XWP-19) — « Remise totale du jour » : Σ remise_montant of the VALIDEE sales of
   * one officine day (`date` YYYY-MM-DD, default today), split by mode and by caissier. P / M read
   * the pharmacy; a CAISSIER gets their own figure (`portee: "CAISSIER"`).
   */
  remises: (params = {}, opts = {}) => api.get("/dashboard/remises", { params, signal: opts.signal }),
  /**
   * Refonte WP-43 (D-06, ux/05 §4.3 / §4.9) — CA réel / CA théorique of a period of officine days
   * (`du` / `au` YYYY-MM-DD, sale date), its previous window (`comparer`), today's pair, « Autres
   * produits » and the glossary's settings. PROPRIETAIRE / MANAGER (margins included).
   */
  revenu: (params = {}, opts = {}) => api.get("/dashboard/revenu", { params, signal: opts.signal }),
};

// ─────────────────────────────────────────────────────────────────────────────
// AVOIR API
// ─────────────────────────────────────────────────────────────────────────────

export const avoirApi = {
  /** Paginated list, optionally filtered by facture_achat_id / e_facture_id (e-facture workspace, WP-29). */
  list: (params = {}, opts = {}) => api.get("/avoirs", { params, signal: opts.signal }),
  /** Single avoir with details */
  getById: (id, opts = {}) => api.get(`/avoirs/${id}`, { signal: opts.signal }),
  /**
   * Create (WP-28 / WP-29): `{ type_avoir, fournisseur_id, date_avoir, reference_fournisseur?, motif,
   * montant_ttc? (ECART / FINANCIER), factures: [{ facture_achat_id, montant_ttc_impute? }], lignes: [{ nature,
   * detail_achat_id, quantite, stock_lot_id?, motif_retour? }], confirmer_quantites_modifiees?, confirmer_depassement? }`.
   */
  create: (body) => api.post("/avoirs", body),
  /** Refused since WP-28 (409 AVOIR_SUPPRESSION_REMPLACEE): an avoir is cancelled, never deleted — use `annuler`. */
  delete: (id) => api.delete(`/avoirs/${id}`),
  /**
   * Refonte WP-28 (M-10, ux/03 §1.6 / §9) — D1 suggestions (no `fournisseur_id`) or the D2 factures of
   * a fournisseur: `{ type: ECART|PRODUITS|FINANCIER, fournisseur_id?, recherche?, ecarts_d_abord?, page?, limit? }`.
   */
  facturesCandidates: (params, opts = {}) => api.get("/avoirs/factures-candidates", { params, signal: opts.signal }),
  /** The gap / returnable lines of the ticked factures (D3): `factureIds` = array of ids. */
  lignesFactures: (factureIds, opts = {}) =>
    api.get("/avoirs/lignes-factures", { params: { factures: factureIds.join(",") }, signal: opts.signal }),
  /**
   * Refonte WP-81 / WP-82 (CP §8.1-§8.2) — the crédit fournisseur's suggested amount for the ticked
   * factures of ONE fournisseur: each facture's positive écart still to credit (`contribution`, `etat`,
   * `cite_par`), their sum (`total_contributions`), `suggestion` (null + `message` when nothing positive).
   */
  suggestionEcart: (fournisseurId, factureIds, opts = {}) =>
    api.get("/avoirs/suggestion-ecart", { params: { fournisseur_id: fournisseurId, factures: factureIds.join(",") }, signal: opts.signal }),
  /**
   * « Réglé » in full or in part (OP-03-08): ristourne → `{ montant, destination_emplacement_id, mode, date?,
   * reference_fournisseur?, notes_paiement? }` (treasury door W16); écart / produits → `{ reference_fournisseur, montant? }`.
   */
  regler: (id, payload) => api.post(`/avoirs/${id}/regler`, payload),
  /**
   * Refonte WP-83 (D-32, A-80, A-104 (4)) — « Confirmer l'avoir » of an Écart / Produits avoir en instance:
   * `{ requete_id, reference_fournisseur?, date?, montant?, note_de_credit_id?, notes_paiement? }` →
   * the avoir CONFIRMÉ (and `reliquat`, the new instance avoir of a partial Écart confirmation).
   */
  confirmer: (id, body) => api.post(`/avoirs/${id}/confirmer`, body),
  /**
   * ux/03 §2.7 (A-02) — the connected fournisseurs' credit notes with their matching state and the
   * counts per state (WP-30): `{ fournisseur_id?, rapprochement?, search?, date_debut?, date_fin?, page?, limit? }`.
   */
  notesFournisseurs: (params = {}, opts = {}) => api.get("/avoirs/notes-fournisseurs", { params, signal: opts.signal }),
  /** OP-03-11 — the avoirs « Rapprocher… » suggests for a note (same facture, then same amount). */
  suggestionsRapprochement: (noteId, opts = {}) => api.get(`/avoirs/notes-fournisseurs/${noteId}/suggestions`, { signal: opts.signal }),
  /** OP-03-11 — match a note: the règlement with `note_de_credit_id` (réf. = the note's number). */
  rapprocher: (id, noteId, payload = {}) => api.post(`/avoirs/${id}/regler`, { ...payload, note_de_credit_id: noteId }),
  /** « Annuler l'avoir » (OP-03-10): number kept, `motif` required. */
  annuler: (id, motif) => api.post(`/avoirs/${id}/annuler`, { motif }),
  /**
   * Trésorerie TWP-57 / TWP-56 — « Imputer le crédit » of a confirmed avoir: the proposal (read-only —
   * the credit not imputed, the open factures with each cap, the oldest-first prefill, the solde that
   * does not move, the right) and the write `{ requete_id, factures: [{ facture_achat_id, montant }] }`
   * (P + M; a replay of the key answers its first result).
   */
  imputation: (id, opts = {}) => api.get(`/avoirs/${id}/imputation`, { signal: opts.signal }),
  imputer: (id, body) => api.post(`/avoirs/${id}/imputer`, body),


  /** CSV export — the journal's validated filters (`listAvoirsSchema`, AUD-AVOIR-29). */
  exportCsv: (filters = {}) => downloadCsv("/avoirs/csv", filters, "avoirs"),
};

// ─────────────────────────────────────────────────────────────────────────────
// NOTIFICATION API
// ─────────────────────────────────────────────────────────────────────────────

export const notificationApi = {
  /** Paginated list. Pass { unread_only: true } to filter */
  list: (params = {}) => api.get("/notifications", { params }),
  /** Lightweight unread count — used by AppShell bell polling */
  count: () => api.get("/notifications/count"),
  /** Mark single notification as read */
  markRead: (id) => api.post(`/notifications/${id}/read`),
  /** Mark all as read */
  markAllRead: () => api.post("/notifications/read-all"),

  // Supplier connection requests (pharmacy-side)
  /** List pending supplier connection requests */
  pendingSuppliers: (params = {}) =>
    api.get("/notifications/suppliers/pending", { params }),
  /** Accept supplier connection request */
  acceptSupplier: (id) => api.patch(`/notifications/suppliers/${id}/accept`),
  /** Reject supplier connection request */
  rejectSupplier: (id) => api.patch(`/notifications/suppliers/${id}/reject`),
};

// ─────────────────────────────────────────────────────────────────────────────
// PRINT API
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Open an authenticated print document in a new tab.
 *
 * Why not just `window.open(url)`? Print routes require a JWT **Bearer header**
 * (server/src/middleware/auth.js) — but `window.open()` performs a plain GET
 * with no Authorization header, so the server returns 401. Worse, a *relative*
 * `/api/v1/...` URL resolves against the SPA host and returns `index.html`
 * (the SPA history-fallback), never reaching the API at all.
 *
 * So we fetch the HTML through the authenticated axios client (Bearer added by
 * the request interceptor, base URL = the real API) and open it as a Blob.
 * No token ever appears in a URL. The server HTML self-triggers window.print()
 * on load. Works identically same-origin or cross-origin (CORS already allows
 * the client origin for every other authenticated call).
 *
 * @param {string} path  API path, e.g. "/print/caisse/<id>/zreport"
 * @returns {Promise<Window|null>}
 */
export async function openPrintDocument(path) {
  // Open the tab synchronously inside the click handler so popup blockers allow it.
  const win = window.open("", "_blank");
  try {
    const html = await api.get(path, { responseType: "text" });
    const body = typeof html === "string" ? html : String(html ?? "");
    const url = URL.createObjectURL(new Blob([body], { type: "text/html" }));
    if (win) {
      win.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } else {
      URL.revokeObjectURL(url);
    }
    return win;
  } catch (e) {
    const msg =
      e?.response?.data?.message ?? e?.message ?? "Erreur lors de l'impression";
    if (win)
      win.document.write(
        `<p style="font-family:system-ui;padding:24px;color:#b00020">Impression indisponible : ${msg}</p>`,
      );
    throw e;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// POSOLOGIE γ API  (structured medication schedule, 1:1 per detail_vente)
// ─────────────────────────────────────────────────────────────────────────────
export const posologieApi = {
  /**
   * Upsert posologies for a validated vente. Backend resolves each item's
   * detail_vente_id by (produit_id, optional stock_lot_id) within the vente.
   *
   * @param {string} vente_id
   * @param {Array<{
   *   produit_id: string, stock_lot_id?: string|null,
   *   matin?: number|null, midi?: number|null, soir?: number|null, nuit?: number|null,
   *   unite?: 'cp'|'gel'|'mL'|'gtte'|'puff'|'sachet',
   *   food_relation?: 'AVANT'|'APRES'|'PENDANT'|'INDIFFERENT'|'A_JEUN',
   *   duration_days?: number|null, note?: string|null,
   *   nom_ar?: string|null, lang?: 'fr'|'ar',
   * }>} items
   */
  upsertForVente: (vente_id, items) =>
    api.post(`/ventes/${vente_id}/posologies`, { items }),

  /**
   * علاجي S03 — save-then-seal: upserts the posologies, creates/updates the
   * scannable session, runs the interaction auto-check, and (when clean)
   * seals + returns the QR payload for the printed sheet (B3/B3.UI).
   */
  savePosologieSession: (vente_id, { items, lang = "fr", seal = true, aliments = [], herbes = [] }) =>
    api.post(`/ventes/${vente_id}/posologie-session`, { items, lang, seal, aliments, herbes }),
};

// ─────────────────────────────────────────────────────────────────────────────
// علاجي — staff-side API (B3.UI/B4/B7/B8: registers, sign-off, claim codes,
// mediated consent, the «Suivi علاجي» panel)
// ─────────────────────────────────────────────────────────────────────────────
export const aalajiApi = {
  /** S06 — read-only register lookup for the Sécurité pickers. */
  registreLookup: (type, q) =>
    api.get(`/posologie-sessions/registres/${type}`, { params: { q } }),

  /** S04 — pharmacist sign-off (bcrypt re-verification, no session created). */
  signoffSession: (session_id, { email, password, note }) =>
    api.post(`/posologie-sessions/${session_id}/signoff`, { email, password, note }),

  /** S05 — explicit seal (refused while alerts are pending). */
  sealSession: (session_id) => api.post(`/posologie-sessions/${session_id}/seal`),

  /** S01 — claim code («one tap, one printed slip»). */
  generateClaimCode: (client_id, email = null) =>
    api.post(`/clients/${client_id}/aalaji/claim-code`, { email }),

  /** The client chip state (B_PH.CLIENT S-CL.1). */
  clientAalajiState: (client_id) => api.get(`/clients/${client_id}/aalaji`),

  /** Counter pairing confirmation (D-3 COMPTOIR channel). */
  confirmPairing: (pairing_code) =>
    api.post(`/clients/aalaji/pairing/confirm`, { pairing_code }),

  /** S14 — pharmacy-mediated caregiver consent (MANAGER+, prints D-6 slip). */
  mediatedConsent: (client_id, { contact, relation }) =>
    api.post(`/clients/${client_id}/aalaji/consentements`, { contact, relation }),

  /** B8 — the «Suivi علاجي» panel (aggregate; detail=1 is consent-gated + audited). */
  clientSuivi: (client_id, detail = false) =>
    api.get(`/clients/${client_id}/aalaji/suivi`, { params: detail ? { detail: 1 } : {} }),

  /** S02 — the pharmacy's stable link code (counter QR / launch kit). */
  pharmacyLinkCode: () => api.get(`/pharmacie/aalaji/link-code`),

  /** S-CL.3 — pending PH-links awaiting counter confirmation (masked identifiers). */
  liensAttente: () => api.get(`/pharmacie/aalaji/liens-attente`),

  /** S-CL.4 — confirm a pending link, optionally attaching a Client fiche. */
  confirmLien: (link_id, client_id = null) =>
    api.post(`/pharmacie/aalaji/liens/${link_id}/confirmer`, client_id ? { client_id } : {}),

  // ── B6/B12 — console, prefs, garde (S07–S13, S15) ──────────────────────
  ordonnances: (statut) => api.get(`/aalaji/ordonnances`, { params: { statut } }),
  ordonnanceAction: (id, action, body = {}) => api.post(`/aalaji/ordonnances/${id}/${action}`, body),
  ordonnancePhotoUrl: (photoId) => `/aalaji/ordonnances/photos/${photoId}`,
  commandes: (tab) => api.get(`/aalaji/commandes`, { params: { tab } }),
  commandeTransition: (id, body) => api.post(`/aalaji/commandes/${id}/transition`, body),
  // EG-19 — one commande's full thread WITH lu_at (staff-scoped). Each row:
  // { id, sens, texte, at, lu_at } — the console renders bubbles + the unread badge.
  commandeMessages: (id) => api.get(`/aalaji/commandes/${id}/messages`),
  commandeMessage: (id, texte, template_key = null) =>
    api.post(`/aalaji/commandes/${id}/messages`, { texte, template_key }),
  // EG-19 — mark the patient→pharmacie thread read when the pharmacist opens it.
  // Sets lu_at on every unread PATIENT message of the commande (server clamps to
  // the caller's pharmacy). Best-effort from the UI: a failure while the server
  // route is still pending is swallowed by the caller, never shown to the operator.
  commandeMessagesRead: (id) => api.post(`/aalaji/commandes/${id}/messages/read`),
  pickupScan: (pickup_code) => api.post(`/aalaji/commandes/pickup-scan`, { pickup_code }),
  // EG-06 — link a RECUPEREE commande to the confirmed counter sale. Writes
  // CommandePatient.vente_id AFTER the vente is validated (not at scan) so an
  // interrupted cashier can never leave a picked-up order with no linked sale
  // (BR-AJ-08 · financial reconciliation). Staff-scoped on the server.
  linkCommandeVente: (id, vente_id) =>
    api.post(`/aalaji/commandes/${id}/vente`, { vente_id }),
  prefs: () => api.get(`/aalaji/preferences`),
  putPrefs: (body) => api.put(`/aalaji/preferences`, body),
  garde: () => api.get(`/aalaji/garde`),
  putGarde: (slots) => api.put(`/aalaji/garde`, { slots }),
};

/** علاجي — ADMIN_PHARMAKON register management (B5: A01–A06). */
export const aalajiAdminApi = {
  list: (type, params) => api.get(`/admin/aalaji/referentiels/${type}`, { params }),
  create: (type, body) => api.post(`/admin/aalaji/referentiels/${type}`, body),
  update: (type, id, body) => api.patch(`/admin/aalaji/referentiels/${type}/${id}`, body),
  retire: (type, id) => api.delete(`/admin/aalaji/referentiels/${type}/${id}`),
  importCsv: (type, { content, filename, apply }) =>
    api.post(`/admin/aalaji/referentiels/${type}/import`, { content, filename, apply }),
};

/**
 * Open a print receipt via `window.open()` using a short-lived signed token.
 *
 * Flow:
 *   1. POST /print/issue-token { resource } — server mints a 60 s JWT scoped
 *      to that exact resource path (PRINT_TOKEN_SECRET, not JWT_SECRET).
 *   2. window.open(`${baseURL}${url_from_server}`) — the server's
 *      `authenticate` middleware accepts the `?token=` query param as a
 *      Bearer-equivalent for /print/* routes (see server/middleware/auth.js).
 *
 * The popup is opened SYNCHRONOUSLY (about:blank) inside the click handler so
 * popup blockers allow it; the real URL is set after the token comes back.
 *
 * @param {string} resource_path API path, e.g. "/print/vente/<id>".
 * @returns {Promise<Window|null>}
 */
export async function openWithToken(resource_path) {
  const win = window.open("", "_blank");
  try {
    const { url } = await api.post("/print/issue-token", {
      resource: resource_path,
    });
    if (!url) throw new Error("Aucun token reçu du serveur");
    // baseURL ends with "/api/v1" and the returned url starts with "/print/…",
    // so concatenation yields the full absolute URL.
    const absolute = `${api.defaults.baseURL}${url}`;
    if (win) {
      win.location.href = absolute;
    }
    return win;
  } catch (e) {
    const msg =
      e?.response?.data?.message ?? e?.message ?? "Erreur lors de l'impression";
    if (win) {
      win.document.write(
        `<p style="font-family:system-ui;padding:24px;color:#b00020">Impression indisponible : ${msg}</p>`,
      );
    }
    throw e;
  }
}

export const printApi = {
  /** Raw ESC/POS binary buffer URL — fetch via axios (Bearer header), then pipe to printer device */
  venteEscpos: (id) => `${api.defaults.baseURL}/print/vente/${id}/escpos`,
  /** Z-report ESC/POS binary URL — fetch via axios (Bearer header) */
  caisseEscpos: (id) =>
    `${api.defaults.baseURL}/print/caisse/${id}/zreport?format=escpos`,

  // ── Authenticated openers — USE THESE from the UI ───────────────────────────
  // The Blob-based opener (openPrintDocument) avoids a token in the URL but
  // breaks if a popup blocker withholds the synchronous Blob URL assignment.
  // The token-based opener (openWithToken) is the canonical path and matches
  // the audit plan; the Blob path is kept as a fallback for back-compat.
  /** Vente receipt (libre / CHIFA) → browser print dialog (token-in-URL) */
  openVente: (id) => openWithToken(`/print/vente/${id}`),
  // (Refonte WP-30: the avoir prints as its A4 document — `documentsApi.ouvrirPdf`, ux/03 §6.1.)
  /** Caisse Z-report (clôture) → browser print dialog (token-in-URL) */
  openCaisseZReport: (id) => openWithToken(`/print/caisse/${id}/zreport`),

  /**
   * Generic token-based opener for any /print/* resource. Use this for new
   * print URLs the UI gains in the future.
   * @param {string} resource_path  API path starting with "/print/"
   */
  openWithToken: (resource_path) => openWithToken(resource_path),
};

// ─────────────────────────────────────────────────────────────────────────────
// USER API  (direct calls — no Zustand store for low-frequency ops)
// ─────────────────────────────────────────────────────────────────────────────

export const userApi = {
  list: (params = {}) => api.get("/users", { params }),
  roleCounts: () => api.get("/users/role-counts"),
  getById: (id) => api.get(`/users/${id}`),
  create: (body) => api.post("/users", body),
  update: (id, body) => api.patch(`/users/${id}`, body),
  // The backend toggleUserSchema requires the target state { actif }. Callers
  // pass the desired next state, e.g. userApi.toggle(id, !user.actif).
  toggle: (id, actif) => api.patch(`/users/${id}/toggle`, { actif }),
  resetPassword: (id, body) => api.post(`/users/${id}/reset-password`, body),
  /** Self-service password change — requires current_password */
  changePassword: (body) => api.post("/users/change-password", body),
  delete: (id) => api.delete(`/users/${id}`),
  /** X XWP-69 — (re)issue the seat payment of an inactive manager / caissier (prorated, server-priced). */
  paymentLink: (id) => api.post(`/users/${id}/payment-link`),
  /** X XWP-69 — what adding a user of `role` costs now (the create form's quote). */
  devisSiege: (role) => api.get("/users/sieges/devis", { params: { role } }),
  /** X XWP-69 — settle a seat payment on return from the checkout (`?checkout_id=`). */
  verifierSiege: (checkout_id) => api.post("/users/sieges/verifier", { checkout_id }),
  /** X XWP-69 — one's own seat line (/moncompte). */
  monSiege: () => api.get("/users/moi/siege"),
  /** X XWP-69 — the team's seats (P / M). */
  sieges: () => api.get("/users/sieges"),
  /** X XWP-67 — « Mes ventes »: one's own sales per period (the amounts follow the cashier visibility policy). */
  mesVentes: () => api.get("/users/moi/ventes"),
  /** X XWP-67 — another user's sales per period (the PROPRIÉTAIRE only). */
  ventesUtilisateur: (id) => api.get(`/users/${id}/ventes`),
  /** X XWP-68 (XWP-66 server) — a user's activity, one's own or (the PROPRIÉTAIRE) another's: `{ data, total, page, limit }`. */
  activite: (id, params = {}) => api.get(`/users/${id}/activite`, { params }),
  /** X XWP-68 (XWP-66 server) — the photo's bytes (oneself, or P / M); 404 without a photo. */
  photo: (id) => api.get(`/users/${id}/photo`, { responseType: "blob" }),
  /** X XWP-68 (XWP-66 server) — set / replace a user's photo (the PROPRIÉTAIRE; multipart field « photo »). */
  definirPhoto: (id, fichier, champ = "photo") => {
    const fd = new FormData();
    fd.append(champ, fichier);
    return api.put(`/users/${id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  },
  /** X XWP-68 (XWP-66 server) — remove a user's photo (the PROPRIÉTAIRE; idempotent, 204). */
  supprimerPhoto: (id) => api.delete(`/users/${id}/photo`),
  /** X XWP-68 (XWP-65 server) — another user's PIN state `{ defini, defini_le, verrouille, verrou_jusqu_a }` (P). */
  pinEtat: (id) => api.get(`/users/${id}/pin`),
  /** X XWP-68 (XWP-65 server) — define / redefine another user's PIN (P; 204). */
  definirPin: (id, pin) => api.put(`/users/${id}/pin`, { pin }),
  /** X XWP-68 (XWP-65 server) — remove another user's PIN (P; 204). */
  supprimerPin: (id) => api.delete(`/users/${id}/pin`),
  /** X XWP-68 (XWP-65 server) — one's own PIN state. */
  monPin: () => api.get("/users/moi/pin"),
  /** X XWP-68 (XWP-65 server) — change one's own PIN, proven by the account password (204). */
  changerMonPin: ({ pin, mot_de_passe }) => api.put("/users/moi/pin", { pin, mot_de_passe }),
};

// ─────────────────────────────────────────────────────────────────────────────
// PHARMACIE API
// ─────────────────────────────────────────────────────────────────────────────

export const pharmacieApi = {
  /** Full settings: pharmacie + licence + role counts */
  getSettings: () => api.get("/pharmacie"),
  /** Partial update — PROPRIETAIRE only */
  update: (body) => api.patch("/pharmacie", body),
  /** Upload a branding logo (multipart, field "logo") — returns updated pharmacie */
  uploadLogo: (file) => {
    const fd = new FormData();
    fd.append("logo", file);
    return api.post("/pharmacie/logo", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  /** Remove the current branding logo */
  removeLogo: () => api.delete("/pharmacie/logo"),
  /** Pending Chargily checkouts for this pharmacy */
  paymentsPending: () => api.get("/pharmacie/payments/pending"),
  /** Paid payment history (newest first) */
  paymentsHistory: () => api.get("/pharmacie/payments/history"),
  /** Regenerate an expired Chargily checkout URL (>20 min old) */
  regeneratePayment: (id) =>
    api.post(`/pharmacie/payments/${id}/regenerate`),
};

// ─────────────────────────────────────────────────────────────────────────────
// AUDIT API — gated user:read (PROPRIETAIRE / MANAGER / ADMIN_PHARMAKON)
// Server scopes responses to req.user.pharmacie_id automatically.
// ─────────────────────────────────────────────────────────────────────────────

export const auditApi = {
  /**
   * Paginated audit list.
   * @param {object} params { page, limit, user_id, entite, action,
   *                          date_debut, date_fin, search }
   */
  list: (params = {}) => api.get("/audit", { params }),
  /** Users dropdown for the user_id filter — scoped to this pharmacy */
  listUsers: () => api.get("/audit/users-list"),

  /** CSV export — same filter params as `list`. */
  exportCsv: (filters = {}) => downloadCsv("/audit/csv", filters, "audit"),
};

// ─────────────────────────────────────────────────────────────────────────────
// ABONNEMENT / MODULES API — Chargily-backed subscription management
// ─────────────────────────────────────────────────────────────────────────────

export const abonnementApi = {
  /**
   * Module catalogue enriched with the pharmacy's active modules + pending
   * checkouts. Response: { data: ModuleEnriched[], historique: Paiement[] }
   */
  listModules: () => api.get("/modules"),
  /**
   * Initiate a Chargily checkout for `code_module`. Returns { checkout_url }
   * which the SPA opens in a new tab.
   */
  acheter: (code_module, duree_mois = 12) =>
    api.post("/modules/acheter", { code_module, duree_mois }),
  /**
   * Verify-on-return: after Chargily redirects the browser back to the
   * env-configured frontend success page with `?checkout_id=...`, call this to
   * settle (activate) the module locally. Idempotent with the async webhook, so
   * it is safe in production too. Returns { settled, statut, code_module }.
   */
  verifierModule: (checkout_id) =>
    api.post("/modules/verify", { checkout_id }),
};

// ─────────────────────────────────────────────────────────────────────────────
// CAISSE API — cash-register sessions
// Permission matrix:
//   read  → PROPRIETAIRE, MANAGER, CAISSIER
//   open  → PROPRIETAIRE, MANAGER, CAISSIER  (own session — server forces
//           caissier_id = req.user.id)
//   close → PROPRIETAIRE, MANAGER, CAISSIER
// ─────────────────────────────────────────────────────────────────────────────

export const caisseApi = {
  /** Caller's currently open session, if any. Returns null when none. */
  current: () => api.get("/caisse/current"),
  /** Trésorerie TWP-58 (TD-350) — the officine's OUVERTE sessions, for the P / M session choice. */
  sessionsActives: () => api.get("/caisse/sessions-actives"),
  /** Paginated list of sessions for this pharmacy. */
  list: (params = {}) => api.get("/caisse", { params }),
  /** Single session detail. */
  getById: (id) => api.get(`/caisse/${id}`),
  /**
   * Open a new session.
   *
   * @param {number|{fonds_ouverture:number, caissier_id?:string}} arg
   *   - number: open for the authenticated user (default flow).
   *   - object: also accepts an optional `caissier_id` (PROPRIETAIRE /
   *     MANAGER may delegate; server enforces RBAC and target validation).
   *
   * The backend rejects with 409 CAISSE_DEJA_OUVERTE when the recipient
   * already has an open session.
   */
  open: (arg) => {
    // Trésorerie TWP-16 — the counted cash is a decimal string (« 10000.00 »), never a float; the
    // session opens on its comptoir (`emplacement_fonds_id`), a different count carries its
    // explanation (`ecart_explication`).
    const body =
      typeof arg === "object" && arg !== null
        ? {
            fonds_ouverture: String(arg.fonds_ouverture),
            ...(arg.caissier_id ? { caissier_id: arg.caissier_id } : {}),
            ...(arg.emplacement_fonds_id ? { emplacement_fonds_id: arg.emplacement_fonds_id } : {}),
            ...(arg.post_licence_id ? { post_licence_id: arg.post_licence_id } : {}),
            ...(arg.ecart_explication ? { ecart_explication: arg.ecart_explication } : {}),
          }
        : { fonds_ouverture: String(arg) };
    return api.post("/caisse/open", body);
  },
  /**
   * Trésorerie TWP-16 (W5) — what the opening shows before the count: the comptoir (TD-150), its
   * standing balance (TD-18 (a)), first opening or not (TDB-04), the comptoirs to choose from.
   */
  ouverture: (params = {}) => api.get("/caisse/ouverture", { params }),
  /**
   * Close `id` with the physically counted cash (Trésorerie TWP-17, W9): one atomic transaction on
   * the server — the session's cash sales, the écart, the versement to the caisse centrale and the
   * frozen snapshot; the answer carries `cloture` (attendu, écart and its classement, versement).
   */
  close: (id, body) => api.post(`/caisse/${id}/close`, body),
  /**
   * Trésorerie TWP-17 (TD-27) — the X (open, « non définitif ») / Z (closed, frozen) data of a
   * session, with the officine's écart tolerance and whether the count is blind for the caller.
   */
  rapport: (id) => api.get(`/caisse/${id}/rapport`),
  /**
   * Trésorerie TWP-19 (TD-27, TD-266) — the audited print of the X / Z: the server records the print
   * and answers the report the A4 document (print layer) is drawn from.
   */
  imprimerRapport: (id, format = "A4") => api.post(`/caisse/${id}/rapport/impressions`, { format }),
  /**
   * Trésorerie TWP-19 — what the close WOULD post for this count (`fonds_cloture`, a decimal string):
   * fonds retenu, versement, non-cash drafts; the écart and « espèces attendues » unless the count is
   * blind for the caller. Read-only.
   */
  apercuCloture: (id, fonds_cloture) => api.post(`/caisse/${id}/cloture/apercu`, { fonds_cloture }),
  /**
   * Trésorerie TWP-19 (W7) — the till of an open session's comptoir: what the dialog needs (session,
   * catalogue motifs per direction, bounds, the drawer's figure unless blind), the advisory preview,
   * and the movement (`{ type, montant, motif_id, note?, idempotency_key }`, espèces only).
   */
  mouvementContexte: (id) => api.get(`/caisse/${id}/mouvements/nouveau`),
  mouvementApercu: (id, body) => api.post(`/caisse/${id}/mouvements/apercu`, body),
  mouvement: (id, body) => api.post(`/caisse/${id}/mouvements`, body),

  // Phase 9 (XpertPharm parity) — session enhancements
  /** Detailed session journal: sales-by-mode + treasury + expected cash + hours. */
  journal: (id) => api.get(`/caisse/${id}/journal`),
  /** Re-activate a closed session (owner / manager). */
  reopen: (id, motif) => api.post(`/caisse/${id}/reopen`, motif ? { motif } : {}),

  /**
   * Caissier "Insights" — the CURRENT cashier's OWN open-session metrics
   * (revenue only, NO cost/margin). Self-scoped server-side to req.user.id.
   * Returns { has_session, caisse_amount, nb_ventes, ca_cosmetique,
   * ca_parapharmacie, ca_medicament, ca_psychotrope }.
   */
  sessionInsights: () => api.get("/caisse/session-insights"),

  /** CSV export — same filter params as `list` (statut, date_debut, date_fin). */
  exportCsv: (filters = {}) => downloadCsv("/caisse/csv", filters, "caisses"),
};

// ─────────────────────────────────────────────────────────────────────────────
// CHIFA API
// ─────────────────────────────────────────────────────────────────────────────

export const chifaApi = {
  /** Lookup by num_enregistrement — local DB first, then CNAS bridge */
  lookup: (num) => api.get("/chifa/lookup", { params: { num } }),
  // X XWP-25 (AUD-BARC-19) — no barcode shortcut here: a scanned code goes to the ONE resolver
  // (`codeApi.resoudre`, GET /codes/resolve); GET /chifa/barcode stays a server route of the
  // CNAS reference (chifa-bridge), never a counter path.
  /** Bulk import from parsed CNAS xlsx — PROPRIETAIRE only */
  importCNAS: (records) => api.post("/chifa/import", { records }),

  /**
   * The Chifa Officine facture of ONE poste (Direction 1, cross-module X XWP-05).
   * Read when the SSE topic `chifa:facture:<poste>` fires (no 5 s poll); `etag`
   * re-validates it (the correction floor): a 304 resolves to `{ inchange: true }`.
   * Otherwise `{ success, data: { source, poste_id, session_ref, session, … } | null, etag }`.
   * `poste` absent = the legacy PRINCIPAL slot. Patient names are display-only.
   * GET /chifa/session/current?poste=
   */
  currentSession: async ({ poste, etag } = {}) => {
    const r = await api.get("/chifa/session/current", {
      params: poste ? { poste } : {},
      headers: etag ? { "If-None-Match": etag } : {},
      validateStatus: (s) => (s >= 200 && s < 300) || s === 304,
    });
    return r && typeof r === "object" ? r : { inchange: true };
  },
  /** GET /chifa/postes — postes with a live facture (poste choice of a workstation, XD-44). */
  postes: () => api.get("/chifa/postes"),

  /**
   * Write the reimbursable basket back toward Chifa Officine (Direction 2, F4).
   * Server re-computes the reimbursement (Decimal) and validates the
   * client-sent totals within tolerance before forwarding to the bridge.
   * Gated `vente:create` (includes CAISSIER).
   * @param {object} payload { vente_id?, detail_fact: [{ num_enr, nom_com,
   *        ppa_plafond, qte, duree, ppa }], totals?, ... }
   * POST /chifa/write
   */
  write: (payload) => api.post("/chifa/write", payload),
};

// ─────────────────────────────────────────────────────────────────────────────
// NOMENCLATURE API — Base Nationale des Produits (MSPRH)
// Powers the ProduitForm autofill + POS autocomplete.
// ─────────────────────────────────────────────────────────────────────────────

export const nomenclatureApi = {
  /**
   * Autocomplete search across nom_marque / dci / num_enregistrement /
   * laboratoire / code. Returns up to `limit` rows (default 20, max 50).
   *
   * Response row shape (per server/src/services/nomenclature.service.js):
   *   { id, num_enregistrement, code, dci, nom_marque, forme, dosage,
   *     conditionnement, liste, laboratoire, pays, type, is_generique,
   *     is_remboursable, statut, date_enregistrement }
   *
   * @param {string} q
   * @param {{ limit?: number, statut_filter?: 'ACTIF'|'RETIRE'|'SUSPENDU' }} [opts]
   */
  search: (q, { limit = 20, statut_filter = "ACTIF", signal } = {}) =>
    api.get("/nomenclature/search", {
      params: { q, limit, statut_filter },
      signal,
    }),
};

// ─────────────────────────────────────────────────────────────────────────────
// STOCK API
// ─────────────────────────────────────────────────────────────────────────────

export const stockApi = {
  /** Paginated lot list with filters */
  listLots: (params = {}, config = {}) => api.get("/stock/lots", { ...config, params }),
  // ─── Cross-module X XWP-09 / XWP-15 — Stock & lots (02_DESIGN §3.1, §3.3) ─────
  /** Disjunctive facets of a view under its filters (each facet without its own). */
  facettes: (params = {}, config = {}) => api.get("/stock/facettes", { ...config, params }),
  /** The topbar entries + the views strip, DB-wide (`{ alertes_stock, lots_perimes, vues, seuils }`). */
  compteurs: (config = {}) => api.get("/stock/alertes/compteurs", config),
  /** « Corrections de stock » — the journal of the quantity corrections (P / M). */
  journalCorrections: (params = {}, config = {}) => api.get("/stock/corrections", { ...config, params }),
  /** A lot's placement across magasins (« Magasin concerné » of the correction dialog, « Répartir »). */
  placementsLot: (lotId, config = {}) => api.get(`/stock/lots/${lotId}/placements`, config),
  /**
   * X XWP-16 — « Répartir »: replace the lot's placement, `[{ magasin_id, quantite, emplacements? }]`
   * (Σ magasins = the lot, XD-96); answers `{ data: placement, touched }`.
   */
  placerLot: (lotId, lignes) => api.put(`/stock/lots/${lotId}/placements`, lignes),
  // ─── X XWP-15 — « Risque de péremption » (ux/04 §2.5, OP-PSA-24) ─────────────
  /** The ranking: `{ horizon_jours, rotation_jours, magasin_id?, sort_by, sort_order, page, limit }`. */
  riskPeremption: (params = {}, config = {}) => api.get("/stock/risque-peremption", { ...config, params }),
  /** CSV of the whole ranking under the same controls (P / M). */
  exportRisqueCsv: (params = {}) => downloadCsv("/stock/risque-peremption/csv", params, "risque-peremption"),
  /** FEFO inversion check during achat line entry → { inversion: boolean }. */
  checkFefoInversion: (produit_id, date_peremption) =>
    api.get("/stock/check-fefo-inversion", {
      params: { produit_id, date_peremption },
    }),
  /** Single lot with full detail */
  getLot: (id, config = {}) => api.get(`/stock/lots/${id}`, config),
  /** Alert counts + full alert rows */
  getAlerts: () => api.get("/stock/alerts"),
  /** Stock correction (requires manager password) */
  correct: (body) => api.post("/stock/corrections", body),
  /** Lot correction history */
  getLotCorrections: (id) => api.get(`/stock/lots/${id}/corrections`),

  // ─── Cross-module X §14 — lot blocks (P/M; XWP-01 API, XWP-13 dialogs) ─────
  /** The lot's figures (« 70 vendables · 30 déjà bloquées · 0 réservées en caisse ») and its blocks. */
  blocagesLot: (lotId, params = {}, opts = {}) =>
    api.get(`/stock/lots/${lotId}/blocages`, { params, signal: opts.signal }),
  /** Block `quantite` units of a lot: { quantite, motif, precision?, magasin_id? } → 409 when not bloquable. */
  bloquerLot: (lotId, body) => api.post(`/stock/lots/${lotId}/blocages`, body),
  /** Lift a block: { motif_levee? }. */
  leverBlocage: (blocageId, body = {}) => api.post(`/stock/blocages/${blocageId}/lever`, body),
  /** X XWP-95 / XWP-97 — « Invisible aux caissiers » of one lot (P only): { invisible_comptoir } → { lot, modifie }. */
  modifierVisibiliteLot: (lotId, invisible_comptoir) => api.patch(`/stock/lots/${lotId}/visibilite`, { invisible_comptoir }),
  // Refonte WP-130 (D-51) — « Mouvements de stock » of the fiche (P / M): { jours } | { debut, fin }, lot_id?
  mouvementsPeriode: (produitId, params = {}, config = {}) =>
    api.get(`/stock/produits/${produitId}/mouvements-periode`, { ...config, params }),

  /** CSV export — produit-level stock snapshot. Same filters as the JSON `produitApi.list`. */
  exportCsv: (filters = {}) => downloadCsv("/stock/csv", filters, "stock"),
  /** CSV export — lot-level breakdown. Same filters as `listLots`. */
  exportLotsCsv: (filters = {}) =>
    downloadCsv("/stock/lots/csv", filters, "stock-lots"),
  /** X XWP-15 — CSV of a product view (« Alertes stock », « Ruptures », « Stock bas »): same grammar as `listLots`. */
  exportProduitsVueCsv: (filters = {}) => downloadCsv("/stock/produits/csv", filters, "stock-produits"),
};

// ─────────────────────────────────────────────────────────────────────────────
// FOURNISSEUR PORTAIL API
// ─────────────────────────────────────────────────────────────────────────────

export const fournisseurPortailApi = {
  // Dashboard
  dashboard: (params = {}) =>
    api.get("/fournisseur-portail/dashboard", { params }),

  // Notifications
  notifs: (params = {}) => api.get("/fournisseur-portail/notifs", { params }),
  markNotifsLue: (ids = []) =>
    api.patch("/fournisseur-portail/notifs/lue", { ids }),

  // Clients (Linked Pharmacies)
  clients: (params = {}) => api.get("/fournisseur-portail/clients", { params }),
  pendingClients: (params = {}) =>
    api.get("/fournisseur-portail/clients/pending", { params }),
  requestConnection: (body) =>
    api.post("/fournisseur-portail/clients/request", body),

  // Catalogue
  listCatalogue: (params = {}) =>
    api.get("/fournisseur-portail/catalogue", { params }),
  upsertCatalogue: (body) => api.post("/fournisseur-portail/catalogue", body),
  deleteCatalogue: (cid) => api.delete(`/fournisseur-portail/catalogue/${cid}`),

  // Pre-commandes (Supplier side)
  listPreCommandes: (params = {}) =>
    api.get("/fournisseur-portail/pre-commandes", { params }),
  respondPC: (id, body) =>
    api.post(`/fournisseur-portail/pre-commandes/${id}/respond`, body),

  // Supplier offers: Commercial workspace only (commercial.api / fournisseurCommercialStore,
  // POST /fournisseur-portail/commercial/offres — X §11.2, XWP-29).
};

// ─────────────────────────────────────────────────────────────────────────────
// E-FACTURE API
// ─────────────────────────────────────────────────────────────────────────────

// ─── E-Factures (Supplier & Pharmacy) ─────────────────────────────────────────
export const efactureApi = {
  // Core E-Facture operations (both supplier & pharmacy)
  list: (params = {}) => api.get("/efactures", { params }),
  getById: (id) => api.get(`/efactures/${id}`),

  // Supplier side - E-Facture operations
  create: (body) => api.post("/efactures", body),
  send: (id) => api.post(`/efactures/${id}/send`),
  markPaid: (id, montant_paye) =>
    api.post(`/efactures/${id}/mark-paid`, { montant_paye }),
  createBL: (id, body) => api.post(`/efactures/${id}/bl`, body),
  updateBL: (id, bl_id, body) =>
    api.patch(`/efactures/${id}/bl/${bl_id}`, body),

  // Pharmacy side - QR/Code import
  importByQR: (qr_code) => api.get(`/efactures/qr/${qr_code}`),
  importByCode: (code) => api.get(`/efactures/import/${code}`),

  // Pharmacy side - Status & updates
  updateStatut: (id, body) => api.patch(`/efactures/${id}/statut`, body),
  demandeModification: (id, notes) =>
    api.post(`/efactures/${id}/demande-modification`, { notes }),
  repondreModification: (id, body) =>
    api.post(`/efactures/${id}/repondre-modification`, body),
  validateStock: (id) => api.post(`/efactures/${id}/validate-stock`),
  listRecu: (params = {}) => api.get("/efactures/recu", { params }),

  // Pharmacy side - Pre-commandes
  createPreCommande: (body) => api.post("/efactures/pre-commandes/new", body),
  mesCommandes: (params = {}) =>
    api.get("/efactures/mes-commandes", { params }),

  // NEW: Pharmacy side - Bon de Réception (BR)
  createBRPharmacy: (id, body) =>
    api.post(`/efactures/${id}/br-pharmacy`, body),

  // NEW: Supplier side - Draft CRUD
  deleteDraft: (id) => api.delete(`/efactures/${id}/draft`),
  updateDraft: (id, body) => api.patch(`/efactures/${id}/draft`, body),
};

// ─────────────────────────────────────────────────────────────────────────────
// AIDE À LA DÉCISION API
// ─────────────────────────────────────────────────────────────────────────────

export const aideDecisionApi = {
  /** Forecast / prevision dashboard (GET /aide-decision/previsions). */
  previsions: () => api.get("/aide-decision/previsions"),

  // Besoin
  getBesoin: (params = {}) => api.get("/aide-decision/besoin", { params }),
  createPrecommandeMulti: (body) => api.post("/aide-decision/besoin/precommande", body),

  // Offres fournisseurs
  getOffres: (params = {}) => api.get("/aide-decision/offres", { params }),
  getOffreDetail: (id) => api.get(`/aide-decision/offres/${id}`),
  // X §15 (XWP-35) — the one award; `body` carries `attributions_confirmees` after an OP-PLAN-07 409.
  accepterOffre: (id, body = {}) => api.post(`/aide-decision/offres/${id}/accepter`, body),
  rejeterOffre: (id, motif) => api.post(`/aide-decision/offres/${id}/rejeter`, { motif }),

  // Offres manuelles
  getOffresManuelles: (params = {}) => api.get("/aide-decision/offres-manuelles", { params }),
  validerOffreManuelle: (id) => api.patch(`/aide-decision/offres-manuelles/${id}/valider`),
  marquerRecu: (id) => api.patch(`/aide-decision/offres-manuelles/${id}/recu`),
  archiver: (id) => api.patch(`/aide-decision/offres-manuelles/${id}/archiver`),

  // Settings
  getSettings: () => api.get("/aide-decision/settings"),
  updateSettings: (body) => api.put("/aide-decision/settings", body),

  // Fournisseur rating
  updateFournisseurNote: (id, note) => api.patch(`/fournisseurs/${id}/note`, { note }),

  // Advanced DSS (2026-06) — explainability + ABC Pareto
  /** Full DSS payload for one product (forecast inputs, scores, 90-day series). */
  explain: (produit_id) => api.get(`/aide-decision/explain/${produit_id}`),
  /** Revenue-Pareto A/B/C classification (?days=1..365, default 90). */
  abc: (params = {}) => api.get("/aide-decision/abc-classification", { params }),

  // Per-product μ overrides (mu_override ∈ (0,10])
  overrides: {
    list: (params = {}) => api.get("/aide-decision/overrides", { params }),
    create: (body) => api.post("/aide-decision/overrides", body),
    update: (id, body) => api.patch(`/aide-decision/overrides/${id}`, body),
    remove: (id) => api.delete(`/aide-decision/overrides/${id}`),
  },

  // Seasonal events (classe_therapeutique multipliers over a date window)
  events: {
    list: (params = {}) => api.get("/aide-decision/seasonal-events", { params }),
    create: (body) => api.post("/aide-decision/seasonal-events", body),
    update: (id, body) => api.patch(`/aide-decision/seasonal-events/${id}`, body),
    remove: (id) => api.delete(`/aide-decision/seasonal-events/${id}`),
  },
};

// ─── DecisionConfig API ──────────────────────────────────────────────────────
/** Advanced DSS knobs (importance tiers, weights, lead-time, holding cost). */
export const decisionConfigApi = {
  get: () => api.get("/aide-decision/config"),
  update: (body) => api.put("/aide-decision/config", body),
  reset: () => api.post("/aide-decision/config/reset"),
};

// ─────────────────────────────────────────────────────────────────────────────
// ADMIN API
// ─────────────────────────────────────────────────────────────────────────────

export const adminApi = {
  /** Register a new pharmacy */
  registerPharmacie: (formData) =>
    api.post("/admin/pharmacies/register", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
};

// ─────────────────────────────────────────────────────────────────────────────
// AUTH API (public endpoints)
// ─────────────────────────────────────────────────────────────────────────────

export const authApi = {
  /** Public fournisseur/laboratoire registration (multipart) */
  registerFournisseur: (formData) =>
    api.post("/auth/register-fournisseur", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  /** Request a password-reset email (forgot-password flow). Always resolves 200. */
  forgotPassword: (email) => api.post("/auth/forgot-password", { email }),
  /** Reset the password using the emailed single-use token. */
  resetPassword: (token, new_password) =>
    api.post("/auth/reset-password", { token, new_password }),
};

// ═════════════════════════════════════════════════════════════════════════════
// FEATURE BATCH 2026-05 — NEW API SURFACES
// ═════════════════════════════════════════════════════════════════════════════

// ─── Client API ──────────────────────────────────────────────────────────────
/**
 * Pharmacy-scoped customer records (Feature 2).
 * Server enforces telephone OR cin presence + uniqueness per pharmacy.
 */
export const clientApi = {
  /**
   * GET /clients — paged search: q, motif, type (CNAS | LIBRE), has_debt, sort_by / sort_order
   * (X XWP-23, the server filters, sorts and counts). `has_debt: true` returns enriched rows with
   * `total_debt`, `oldest_debt_date`, `cards_held_count`. `opts.signal` cancels a superseded read.
   */
  list: (params = {}, opts = {}) => api.get("/clients", { params, signal: opts.signal }),
  /** Convenience: list clients that currently owe money (Crédit page). */
  listWithDebt: (params = {}) =>
    api.get("/clients", { params: { ...params, has_debt: true } }),
  /** GET /clients/:id — detail with ventes + custody history. */
  get: (id) => api.get(`/clients/${id}`),
  /**
   * Batch-1 §11 — GET /clients/:id/historique — the unified patient panel:
   * résumé chiffré + série mensuelle, fidélité, historique d'achats, top-5 des
   * médicaments avec fréquence (Q7) et stock du même produit ET de la même DCI.
   *
   * Open to every pharmacy role (RBAC `client:historique`). Carries NOTHING
   * clinical: the علاجي suivi keeps its own consent-gated endpoint.
   * `mois` is the sliding window (server default 24, bounded 1..120).
   */
  historique: (id, mois) =>
    api.get(`/clients/${id}/historique`, {
      params: mois ? { mois } : {},
    }),
  /** POST /clients — telephone OR cin required. */
  create: (body) => api.post("/clients", body),
  /**
   * X §4.2 — GET /clients/prefill-chifa: the review payload of the poste's current CHIFA facture
   * (names, motif proposé, CHIFA block, existing client). 422 CHIFA_SESSION_EXPIREE when stale.
   */
  prefillChifa: ({ poste, session_ref }) =>
    api.get("/clients/prefill-chifa", { params: { poste, session_ref } }),
  /**
   * X §4.2 — POST /clients { source: "CHIFA", session_ref, poste, nom, prenom?, telephone?, motif? }.
   * The insured's identity is re-read by the server from the session. 409 CLIENT_CHIFA_EXISTANT
   * carries `details.client` (« Sélectionner »).
   */
  createDepuisChifa: ({ session_ref, poste, nom, prenom, telephone, motif }) =>
    api.post("/clients", { source: "CHIFA", session_ref, poste, nom, prenom, telephone, motif }),
  /** PATCH /clients/:id — partial. */
  update: (id, body) => api.patch(`/clients/${id}`, body),
  /** DELETE /clients/:id — soft delete. Refuses if outstanding debt. */
  remove: (id) => api.delete(`/clients/${id}`),
  /** Autocomplete shorthand for the ClientSelector in CaisseLibre / VenteChifa. */
  search: (q, limit = 10) =>
    api.get("/clients", { params: { q, limit } }),

  /** CSV export — same filter and sort params (q, motif, type, has_debt, sort_by / sort_order) as `list`. */
  exportCsv: (filters = {}) => downloadCsv("/clients/csv", filters, "clients"),
};

// ─── Crédit API ──────────────────────────────────────────────────────────────
/**
 * Crédit (deferred payment) lifecycle (Feature 2 + 3).
 * CREDIT ventes share the same `ventes` table; this API surfaces the
 * subset filtered by type_vente=CREDIT plus CardCustody management.
 *
 * Backend base path is now /credits; the old /instances path is kept as a
 * deprecated alias for one release (the name "Instance à terme" is being
 * reused by the CHIFA feature).
 */
export const creditApi = {
  /**
   * GET /credits — the crédit journal (X §6.4 · XWP-20): `q`, `etat` (comma list of ETAT_CREDIT;
   * none = « à recouvrer »), `client_id`, `date_debut` / `date_fin`, `sort_by` / `sort_order`, `page`,
   * `limit` → `{ data, total, page, limit, etats, compteurs, totaux }`. The legacy `statut`
   * (unpaid | paid | all) is still read by the server.
   */
  list: (params = {}, opts = {}) => api.get("/credits", { params, signal: opts.signal }),
  /** GET /credits/:id — the drawer: summary, client KPI, lines, cards, the modes this user may record. */
  get: (id, opts = {}) => api.get(`/credits/${id}`, { signal: opts.signal }),
  /** GET /credits/:id/versements — the « Versements » tab (oldest first, paged, contre-passation verdict per row). */
  versements: (id, params = {}, opts = {}) => api.get(`/credits/${id}/versements`, { params, signal: opts.signal }),
  /** GET /credits/:id/historique-client — the client's other crédits (paged). */
  historiqueClient: (id, params = {}, opts = {}) => api.get(`/credits/${id}/historique-client`, { params, signal: opts.signal }),
  /**
   * POST /credits/:id/versements — one versement (X §6.1): `{ montant, mode, reference?,
   * destination_emplacement_id? (chèque / virement / carte, P/M), idempotency_key, notes? }`.
   */
  verser: (id, body) => api.post(`/credits/${id}/versements`, body),
  /** POST /credits/versements/:id/contre-passer — `{ motif, idempotency_key? }` (P/M, XD-42). */
  contrePasser: (versementId, body) => api.post(`/credits/versements/${versementId}/contre-passer`, body),
  /** GET /clients/:id/exposition-credit — encours / plafond / disponible of a client (X §6.5). */
  exposition: (clientId, opts = {}) => api.get(`/clients/${clientId}/exposition-credit`, { signal: opts.signal }),
  /**
   * POST /credits — open a new Crédit.
   * Body shape:
   *   { client_id, caisse_id?, carte_chifa_retenue: boolean, carte_numero?,
   *     details: [{ stock_lot_id, produit_id, quantite, prix_unitaire, remise? }],
   *     notes? }
   */
  create: (body) => api.post("/credits", body),
  /**
   * POST /credits/:id/mark-paid — « Verser le solde restant » (X §6.2): one versement of exactly the
   * remaining balance. Body: `{ idempotency_key, mode?, reference?, destination_emplacement_id?, notes? }`.
   */
  markPaid: (id, body = {}) =>
    api.post(`/credits/${id}/mark-paid`, body),
  /**
   * POST /credits/return-card — return a HELD CHIFA card.
   * Body: { custody_id, notes? }
   * Refuses if the linked vente is still unpaid.
   */
  returnCard: (body) => api.post("/credits/return-card", body),
  /** GET /credits/custodies — list CardCustody rows. */
  listCustodies: (params = {}, opts = {}) => api.get("/credits/custodies", { params, signal: opts.signal }),

  /** CSV export — the journal's filter (q, etat, client_id, date_debut, date_fin, sort) as `list`. */
  exportCsv: (filters = {}) =>
    downloadCsv("/credits/csv", filters, "credits"),
};

/**
 * Transitional alias — kept for one release while callers migrate from
 * `instanceApi` to `creditApi`. Points at the SAME /credits endpoints.
 * @deprecated use `creditApi`.
 */
export const instanceApi = creditApi;

// ─── Retour API ──────────────────────────────────────────────────────────────
/**
 * Retour (F2) — customer product return, reverse of a vente (stock goes BACK
 * into stock). One creation path (XWP-78, XO-21 — every return is linked to a vente; the server
 * refuses `POST /retours/standalone` with 422 RETOUR_VENTE_REQUISE):
 *   from-vente  → linked to an existing VALIDEE/RETOURNEE vente; pull original
 *                 DetailVente lines and return some/all (PARTIAL allowed). Stock
 *                 is restored to the original DetailVente.stock_lot_id when
 *                 traceable, else a new lot is created. The vente flips to
 *                 RETOURNEE when fully returned.
 *
 * The replacement sale (e.g. Doliprane → Efferalgan) is a SEPARATE normal sale
 * processed at /caisse — there is intentionally no combined endpoint.
 *
 * Base path /retours. The api.client interceptor unwraps `res.data`, so list
 * calls expose `r.data` (array) + `r.total`, single calls `r.data` (object).
 */
export const retourApi = {
  /**
   * GET /retours — paged. Filters:
   *   q?, type_retour: 'LIE_VENTE'|'STANDALONE'|'all', vente_id?, client_id?,
   *   date_debut?, date_fin?, page, limit, sort_by, sort_order.
   * `opts.signal` cancels a superseded request (the list's request sequence, XMD-CP-37).
   */
  list: (p, opts = {}) => api.get("/retours", { params: p, signal: opts.signal }),
  /** GET /retours/:id — retour + lignes(+produit,stock_lot,detail_vente) + vente + client + caissier. */
  getById: (id) => api.get(`/retours/${id}`),
  /**
   * POST /retours/from-vente — return lines off an existing vente.
   * Body: { vente_id, caisse_id?, motif (5..500), notes?,
   *         lignes: [{ detail_vente_id, quantite (1..9999) }] }
   */
  createFromVente: (b) => api.post("/retours/from-vente", b),
  /**
   * POST /retours/echange/apercu — cross-module XWP-79: the server-computed preview of a return / échange
   * draft (writes nothing): `lignes_retour[]` (montant = what the patient paid for those units),
   * `remplacements[]` (prix_unitaire by the one resolver, source, ppa_produit), total_retour (R),
   * total_remplacement (S), net = S − R, montant_a_regler, direction CLIENT_PAIE | A_RENDRE_ESPECES and its
   * label; decimal strings. Body = the create's: { vente_id, caisse_id?, lignes, replacements? }.
   */
  apercuEchange: (b, opts = {}) => api.post("/retours/echange/apercu", b, { signal: opts.signal }),
};

// ─── Instance à terme CHIFA API ────────────────────────────────────────────────
/**
 * Instance à terme CHIFA (F1) — the no-prescription / has-CHIFA-card deferred
 * sale, linked to the Vente CHIFA settlement path.
 *
 * Lifecycle (server `statut_instance` on the underlying Vente row):
 *   PENDING     → created at the counter; stock decremented NOW, debt recorded
 *                 (montant_du), no payment.
 *   SENT_CHIFA  → drug data pushed through the CHIFA bridge (patient returned
 *                 with the prescription).
 *   SETTLED     → validated as a Vente CHIFA (one-to-one VenteChifa header
 *                 created from the instance's own lines; NO second destock).
 *
 * Base path /instances-chifa. The api.client interceptor unwraps `res.data`,
 * so list calls expose `r.data` (array) + `r.total`, single calls `r.data`
 * (object).
 */
export const instanceChifaApi = {
  /**
   * GET /instances-chifa — the journal (X §7 · XWP-21): `q`, `etat` (comma list of ETAT_INSTANCE or
   * `TOUS`; none = the open instances), `client_id`, `sort_by` / `sort_order`, `page`, `limit` →
   * `{ data, total, page, limit, etats, compteurs }` (rows carry their derived `etat`). The legacy
   * `statut` (PENDING | SENT_CHIFA | SETTLED | all) is still read.
   */
  list: (p = {}, opts = {}) => api.get("/instances-chifa", { params: p, signal: opts.signal }),
  /**
   * GET /instances-chifa/:id — the drawer: vente + details + client + vente_chifa?, its `etat`,
   * `modification` ({ possible, raison }), the `actions` of this user, `cartes` (with `a_rendre`) and
   * `historique` (audit rows with motif and before / after).
   */
  getById: (id, opts = {}) => api.get(`/instances-chifa/${id}`, { signal: opts.signal }),
  /** PATCH /instances-chifa/:id/lignes/:ligneId — `{ quantite?, stock_lot_id?, motif }` (P/M, PENDING). */
  modifierLigne: (id, ligneId, body) => api.patch(`/instances-chifa/${id}/lignes/${ligneId}`, body),
  /** DELETE /instances-chifa/:id/lignes/:ligneId — `{ motif }` (P/M, PENDING, not the last line). */
  retirerLigne: (id, ligneId, body) => api.delete(`/instances-chifa/${id}/lignes/${ligneId}`, { data: body }),
  /** DELETE /instances-chifa/:id — « Supprimer l'instance », `{ motif }` (P/M, PENDING; number kept). */
  supprimer: (id, body) => api.delete(`/instances-chifa/${id}`, { data: body }),
  /**
   * POST /instances-chifa/:id/verification-vente-libre (X §8.1, XWP-22) — may it be paid as a Vente
   * libre now: `{ eligible, raison, lignes: [{ ok, code, raison, … }] }`. Read only (P / M / C).
   */
  verifierVenteLibre: (id) => api.post(`/instances-chifa/${id}/verification-vente-libre`),
  /**
   * POST /instances-chifa — open a new instance (decrements stock).
   * Body: { client_id (REQUIRED), caisse_id?, details:[{ stock_lot_id,
   *   produit_id, quantite, prix_unitaire_ppa, tarif_remb?, num_enr_cnas?,
   *   is_remboursable }], notes? }
   */
  create: (b) => api.post("/instances-chifa", b),
  /**
   * POST /instances-chifa/:id/send-chifa — flip PENDING → SENT_CHIFA and get
   * back `{ vente, bridgePayload:{ lines:[{nom,num_enr,quantite,ppa,tr,…}] } }`
   * so the SPA can POST the payload to the local CHIFA bridge.
   */
  sendToChifa: (id) => api.post(`/instances-chifa/${id}/send-chifa`),
  /**
   * POST /instances-chifa/:id/settle — validate as a Vente CHIFA.
   * Body: { chifa: <chifaHeaderSchema>, caisse_id? }. Server recomputes CHIFA
   * totals from the EXISTING lines, creates the VenteChifa header, flips
   * statut_instance SETTLED + paid_at. Stock is NOT decremented again.
   */
  settle: (id, b) => api.post(`/instances-chifa/${id}/settle`, b),
  /**
   * POST /instances-chifa/from-vente — Phase 2 "Différer la partie CHIFA".
   * Splits a live Vente CHIFA: non-reimbursable lines paid now as LIBRE, the
   * reimbursable lines' Part Assuré deferred as an INSTANCE_CHIFA (+ mandatory
   * CHIFA-card custody). Requires an open caisse. Returns { instance, libre, custody }.
   * Body: { client_id, taux_pec, carte_numero, chifa?:{ no_fact_chifa, no_bordereau?,
   *   centre?, date_soins? }, details:[{ stock_lot_id, produit_id, quantite,
   *   prix_unitaire_ppa, tarif_remb?, num_enr_cnas?, is_remboursable }], notes? }
   */
  deferFromChifa: (b) => api.post("/instances-chifa/from-vente", b),
};

// ─── Rapport API ─────────────────────────────────────────────────────────────
/**
 * État de rotation report (Feature 6).
 *
 * The server returns:
 *   format=html  → text/html      (Blob with html MIME — open in new tab)
 *   format=pdf   → application/pdf (Blob — anchor download)
 *   format=xlsx  → spreadsheetml   (Blob — anchor download)
 *
 * All three formats are fetched as `responseType: 'blob'` so the caller has a
 * single uniform Blob to handle. HTML pages can be opened via
 * `window.open(URL.createObjectURL(blob), '_blank')`; PDF/XLSX trigger a
 * download via a temporary `<a download>`.
 */
export const rapportApi = {
  /**
   * Generate the État de rotation report.
   *
   * @param {object} payload
   * @param {number} payload.lookback_months  1–24
   * @param {string[]} [payload.laboratoires] optional whitelist
   * @param {string[]} payload.produit_ids    1–500 UUIDs
   * @param {'html'|'pdf'|'xlsx'} [payload.format='html']
   * @returns {Promise<Blob>}                 Blob ready for window.open / download
   */
  etatRotation: (payload) =>
    api.post("/rapports/etat-rotation", payload, { responseType: "blob" }),

  /**
   * État de rotation — interactive JSON payload (redesigned page).
   * Returns { success, rows, summary, meta } for the KPI strip, ABC table,
   * expandable lot rows and sticky footer. `produit_ids` may be empty/omitted
   * to analyse the whole catalogue.
   *
   * @param {object} payload
   * @param {number} payload.lookback_months  1–24
   * @param {string[]} [payload.laboratoires] optional whitelist
   * @param {string[]} [payload.produit_ids]  empty/omitted = whole catalogue
   * @returns {Promise<{success:boolean, rows:object[], summary:object, meta:object}>}
   */
  etatRotationData: (payload) =>
    api.post("/rapports/etat-rotation", { ...payload, format: "json" }),

  /** GET /rapports/laboratoires — distinct labels for the filter dropdown. */
  laboratoires: () => api.get("/rapports/laboratoires"),
};

// ─── Analytics / BI API (Phase 3) ────────────────────────────────────────────
/**
 * Read-only enterprise analytics. Each domain method returns
 * { success, data, meta }. Period params: { from:'YYYY-MM-DD', to:'YYYY-MM-DD' }.
 */
export const analyticsApi = {
  commercial: (params) => api.get("/analytics/commercial", { params }),
  stock: (params) => api.get("/analytics/stock", { params }),
  dciRotation: (params) => api.get("/analytics/dci-rotation", { params }),
  financial: (params) => api.get("/analytics/financial", { params }),
  suppliers: (params) => api.get("/analytics/suppliers", { params }),
  compliance: (params) => api.get("/analytics/compliance", { params }),
  /** Feature 2026-06 — returns & exchanges domain (KPIs, trends, top products). */
  retours: (params) => api.get("/analytics/retours", { params }),
  /** MANAGER-visible domain subset (+ full list). */
  getVisibility: () => api.get("/analytics/visibility"),
  /** PROPRIETAIRE sets the MANAGER-visible subset. */
  setVisibility: (visible_domains) =>
    api.put("/analytics/visibility", { visible_domains }),
  /** Feature batch 4 — médecin analytics: ranked list + summary for the dashboard. */
  medecins: (params) => api.get("/analytics/medecins", { params }),
  /** Feature batch 4 — single-médecin analytics detail (periode=3m|12m|all). */
  medecinDetail: (id, params) =>
    api.get(`/analytics/medecins/${id}`, { params }),
};

// ─── CNAS Base API ───────────────────────────────────────────────────────────
/** CNAS reference lookup by `num_enregistrement`. */
export const cnasBaseApi = {
  lookup: (num) =>
    api.get("/cnas-base/lookup", { params: { num_enregistrement: num } }),
};

// ─── Code resolver (refonte WP-11 / WP-12 / WP-24) ─────────────────────────────
/**
 * The ONE exact code resolver (spec §G, A-08; ux/04 §4.2): a scanned or typed code → LOT |
 * PRODUIT | AMBIGU | INCONNU for the caller's pharmacy (`GET /codes/resolve`). Never the typeahead.
 */
export const codeApi = {
  resoudre: (code, opts = {}) =>
    api.get("/codes/resolve", { params: { code, ...(opts.canal && { canal: opts.canal }) }, signal: opts.signal }),
};

// ─── Laboratoire API ─────────────────────────────────────────────────────────
/** Full laboratoire list for dropdown / filter selectors. */
export const laboratoireApi = {
  /**
   * List labs. Pass `{ scope: "in_use" }` to get only labs referenced by this
   * pharmacy's live produits (deduped) — used by the catalogue/stock filter
   * chips. Omitting params returns the full visible pool (back-compatible).
   *
   * @param {{ scope?: 'all'|'tenant'|'global'|'in_use', q?: string, limit?: number }} [params]
   */
  list: (params) => api.get("/laboratoires", { params }),
  /** Convenience: only labs actually in use by this pharmacy's produits. */
  inUse: () => api.get("/laboratoires", { params: { scope: "in_use" } }),
};

// ─── Phase 1 (XpertPharm parity) — Médecin directory ──────────────────────────
/** Prescriber directory for the psychotrope dispensing flow. */
export const medecinApi = {
  list: (params = {}) => api.get("/medecins", { params }),
  /**
   * Autocomplete (Feature batch 4) — server filters by nom/prénom/spécialité when
   * `q` has 2+ chars; returns lean { id, nom, prenom, specialite, wilaya } rows.
   */
  dropdown: (q) =>
    api.get("/medecins/dropdown", { params: q ? { q } : undefined }),
  getById: (id) => api.get(`/medecins/${id}`),
  create: (body) => api.post("/medecins", body),
  update: (id, body) => api.put(`/medecins/${id}`, body),
  delete: (id) => api.delete(`/medecins/${id}`),
};

// ─── Phase 1 (XpertPharm parity) — Ordonnancier (psychotrope register) ────────
// The journal, corrections, Annexes and the ordonnancier v2 dispensation fields and attachments
// (X XWP-54 / XWP-55) live in their own client — one definition, re-exported here for the callers.
export { ordonnancierApi } from "./ordonnancier.api.js";

// ─── Phase 2 (XpertPharm parity) — Shortage & reorder ─────────────────────────
/** Shortage journal + order assistant. */
export const manquantApi = {
  list: (params = {}) => api.get("/manquants", { params }),
  create: (body) => api.post("/manquants", body),
  traiter: (id) => api.put(`/manquants/${id}/traiter`),
  ignorer: (id) => api.put(`/manquants/${id}/ignorer`),
  orderAssistant: (params = {}) =>
    api.get("/manquants/order-assistant", { params }),
  generateOrder: (body) => api.post("/manquants/order-assistant/generer", body),
};

// ─── Phase 3 (XpertPharm parity) — Product tags ───────────────────────────────
/** Tag CRUD + produit assignment. */
export const tagApi = {
  list: () => api.get("/tags"),
  create: (body) => api.post("/tags", body),
  update: (id, body) => api.put(`/tags/${id}`, body),
  delete: (id) => api.delete(`/tags/${id}`),
  assignToProduit: (produitId, tag_ids) =>
    api.post(`/produits/${produitId}/tags`, { tag_ids }),
};

// ─── Phase 5 (XpertPharm parity) — Loyalty programme ──────────────────────────
/** Loyalty cards, points ledger, barème, and POS lookup. Earn/redeem ride
 *  vente:validate (POST /ventes/:id/validate with body.fidelite). */
export const fideliteApi = {
  // Barème (per-pharmacie singleton)
  getBareme: () => api.get("/fidelite/bareme"),
  updateBareme: (body) => api.put("/fidelite/bareme", body),
  // Cards
  listCartes: (params = {}) => api.get("/fidelite/cartes", { params }),
  getCarte: (id) => api.get(`/fidelite/cartes/${id}`),
  createCarte: (body) => api.post("/fidelite/cartes", body),
  movements: (id) => api.get(`/fidelite/cartes/${id}/movements`),
  correction: (id, body) => api.post(`/fidelite/cartes/${id}/correction`, body),
  // POS lookup by card number → { carte, bareme }
  lookup: (numero) => api.get("/fidelite/lookup", { params: { numero } }),
};

// ─── Phase 7 (XpertPharm parity) — Sales documents (BL + Facture Vente) ────────
/** Counter-sale delivery notes derived from validated sales. */
export const bonLivraisonVenteApi = {
  list: (params = {}) => api.get("/bons-livraison", { params }),
  getById: (id) => api.get(`/bons-livraison/${id}`),
  createFromVente: (vente_id, notes = null) =>
    api.post("/bons-livraison/from-vente", { vente_id, notes }),
  cancel: (id) => api.post(`/bons-livraison/${id}/cancel`),
};

/** Formal sales invoices with TVA/TAP/G50 breakdown + lifecycle. */
export const factureVenteApi = {
  list: (params = {}) => api.get("/factures-vente", { params }),
  getById: (id) => api.get(`/factures-vente/${id}`),
  create: (body) => api.post("/factures-vente", body), // { bl_ids, tap_pct, montant_reporte, client_id, notes }
  validate: (id) => api.post(`/factures-vente/${id}/validate`),
  close: (id) => api.post(`/factures-vente/${id}/close`),
  reopen: (id) => api.post(`/factures-vente/${id}/reopen`),
  pay: (id) => api.post(`/factures-vente/${id}/pay`),
  cancel: (id) => api.post(`/factures-vente/${id}/cancel`),
  /** Open the A4 invoice (authenticated Blob → browser print dialog). */
  print: (id) => openPrintDocument(`/factures-vente/${id}/print`),
};

// ─── Phase 8 (XpertPharm parity) — Treasury & cash management ──────────────────
/** Third parties (suppliers / customers / other) + per-tiers journal. */
export const tiersApi = {
  list: (params = {}) => api.get("/tiers", { params }),
  getById: (id) => api.get(`/tiers/${id}`),
  create: (body) => api.post("/tiers", body),
  update: (id, body) => api.put(`/tiers/${id}`, body),
  journal: (id) => api.get(`/tiers/${id}/journal`),
};

/** Cash ledger: encaissement / décaissement / transfer + balance. */
export const tresorerieApi = {
  movements: (params = {}) => api.get("/tresorerie/mouvements", { params }),
  encaissement: (body) => api.post("/tresorerie/encaissements", body),
  decaissement: (body) => api.post("/tresorerie/decaissements", body),
  transfer: (body) => api.post("/tresorerie/transferts", body),
  cancel: (id, motif = null) => api.post(`/tresorerie/mouvements/${id}/annuler`, { motif }),
  balance: (caisseId) => api.get(`/tresorerie/caisses/${caisseId}/solde`),
};

// ─── Batch-1 vague 3 (2026-08) — Trésorerie & Finance d'officine ──────────────
/**
 * La CAISSE CENTRALE de l'officine — le compte permanent qui porte le chiffre
 * d'affaires — ses emplacements de fonds, son journal, ses charges et ses
 * règlements d'achat.
 *
 * Surface DISTINCTE de `tresorerieApi` ci-dessus, qui est le journal de SESSION
 * du comptoir : deux périmètres RBAC, deux clients. Les mélanger dans un seul
 * objet aurait invité un écran de comptoir à appeler une route de propriétaire.
 *
 * PLAN_pharmacy_batch1_modifications §3.
 */
export const tresoreriePharmacieApi = {
  // Le référentiel (types de charge, types d'emplacement, périodes et axes
  // d'analyse) est SERVI par le serveur, jamais recopié dans le bundle : une
  // seule liste, celle de `config/tresorerie-pharmacie.config.js` (L-2).
  referentiel: () => api.get("/tresorerie-pharmacie/referentiel"),
  cockpit: () => api.get("/tresorerie-pharmacie/caisse-centrale"),
  /**
   * TWP-11 — le plancher de correction du store de trésorerie (TD-60) : la version de trésorerie
   * de l'officine, GET conditionnel (`If-None-Match`, 304 sans corps quand rien n'a bougé).
   * @returns {Promise<{ inchange: true } | { version_tresorerie: string, etag: string, verifie_at: string, sujet: string, plancher_ms: { visible: number, cache: number } }>}
   */
  version: async ({ etag } = {}) => {
    const r = await api.get("/tresorerie-pharmacie/version", {
      headers: etag ? { "If-None-Match": etag } : {},
      validateStatus: (s) => (s >= 200 && s < 300) || s === 304,
    });
    return r && typeof r === "object" && r.data ? r.data : { inchange: true };
  },

  emplacements: (params = {}) => api.get("/tresorerie-pharmacie/emplacements", { params }),
  createEmplacement: (body) => api.post("/tresorerie-pharmacie/emplacements", body),
  updateEmplacement: (id, body) => api.put(`/tresorerie-pharmacie/emplacements/${id}`, body),
  setEmplacementActif: (id, is_actif) =>
    api.post(`/tresorerie-pharmacie/emplacements/${id}/actif`, { is_actif }),

  mouvements: (params = {}) => api.get("/tresorerie-pharmacie/mouvements", { params }),
  encaissement: (body) => api.post("/tresorerie-pharmacie/encaissements", body),
  decaissement: (body) => api.post("/tresorerie-pharmacie/decaissements", body),
  transfert: (body) => api.post("/tresorerie-pharmacie/transferts", body),
  // L-1 — on corrige par contre-passation, jamais par suppression.
  contrepasserMouvement: (id, motif) =>
    api.post(`/tresorerie-pharmacie/mouvements/${id}/contre-passer`, { motif }),
  updateMouvement: (id, body) => api.put(`/tresorerie-pharmacie/mouvements/${id}`, body),
  // Q5 — rejeu de la remontée de clôture (idempotent côté base).
  rejouerRemontee: (caisseId) => api.post(`/tresorerie-pharmacie/remontees/${caisseId}`),

  charges: (params = {}) => api.get("/tresorerie-pharmacie/charges", { params }),
  charge: (id) => api.get(`/tresorerie-pharmacie/charges/${id}`),
  createCharge: (body) => api.post("/tresorerie-pharmacie/charges", body),
  updateCharge: (id, body) => api.put(`/tresorerie-pharmacie/charges/${id}`, body),
  payerCharge: (id, body) => api.post(`/tresorerie-pharmacie/charges/${id}/payer`, body),
  annulerCharge: (id, motif) => api.post(`/tresorerie-pharmacie/charges/${id}/annuler`, { motif }),

  // TWP-25 / TWP-26 — G3 « Journal des règlements » (one row per settlement, the totals of the whole
  // filtered set, keyset `apres`) and G6 / E-13 the facture's settlement block (imputations with their
  // operation, avoirs, legs, e-facture payments, the « Régler » gate).
  reglements: (params = {}) => api.get("/tresorerie-pharmacie/reglements", { params }),
  ledgerAchat: (factureId) => api.get(`/tresorerie-pharmacie/achats/${factureId}/reglements`),
  // TWP-28 — no client of the legacy one-facture settlement routes (`POST /achats/:id/regler`, `PUT /reglements/:id`,
  // `POST /reglements/:id/contre-passer`): every settlement of the SPA is the one form (`creerReglement`, G5 edit / cancel
  // through `/operations/:id`) — no second settlement path (02_ARCHITECTURE §6.6). The server wrappers go with TWP-44.

  analytiqueCouts: (params = {}) => api.get("/tresorerie-pharmacie/analytique/couts", { params }),
  // TWP-38 — one bloc of figures (`bloc`, `periode`…; each figure `{ valeur, definition_id, filtre }`).
  kpis: (params = {}) => api.get("/tresorerie-pharmacie/analytique/kpis", { params }),

  // ─── TWP-15 / TWP-18 — « Comptes & caisses » (the canonical accounts API, decimal strings both ways) ─
  /** The tree: groups, accounts, the footer (`GET /comptes`, one request, no per-row call). */
  comptes: (params = {}) => api.get("/tresorerie-pharmacie/comptes", { params }),
  /** The drawer of one account: the account, its parent, its children, its opening operation. */
  compte: (id) => api.get(`/tresorerie-pharmacie/comptes/${id}`),
  /** The free CAISSE posts the comptoir form may bind (P). */
  postesCaisse: () => api.get("/tresorerie-pharmacie/comptes/postes-caisse"),
  creerCompte: (body) => api.post("/tresorerie-pharmacie/comptes", body),
  /** PUT with the account's configuration version in If-Match (409 when someone changed it). */
  modifierCompte: (id, body, version) =>
    api.put(`/tresorerie-pharmacie/comptes/${id}`, body, { headers: { "If-Match": `"${version}"` } }),
  changerEtatCompte: (id, is_actif, version) =>
    api.post(`/tresorerie-pharmacie/comptes/${id}/actif`, { is_actif }, version != null ? { headers: { "If-Match": `"${version}"` } } : {}),
  /** The account's legs, newest first, with the running balance (keyset `apres`). */
  ecrituresCompte: (id, params = {}) => api.get(`/tresorerie-pharmacie/comptes/${id}/ecritures`, { params }),
  serieCompte: (id, params = {}) => api.get(`/tresorerie-pharmacie/comptes/${id}/serie`, { params }),
  /** TWP-10 — the operations list (journal projection: `type`, `compte_id`, keyset `apres`). */
  operations: (params = {}) => api.get("/tresorerie-pharmacie/operations", { params }),
  /**
   * TWP-29 — the journal's filtered set as a file (`format=csv|xlsx`, the SAME query as the screen, every
   * page, the totals block): a Blob; a refusal (422 above the synchronous bound) comes back as a Blob too.
   */
  exporterOperations: (params = {}) =>
    api.get("/tresorerie-pharmacie/operations/export", { params, responseType: "blob", timeout: 60_000 }),
  /** TWP-18 — the « Aperçu » of a new account's opening (parent / source before and after). */
  apercuCompte: (body) => api.post("/tresorerie-pharmacie/comptes/apercu", body),
  /** TWP-18 — the day of a comptoir (`date` = AAAA-MM-JJ, default today): sessions, résumé, frise. */
  journeeComptoir: (id, params = {}) => api.get(`/tresorerie-pharmacie/comptoirs/${id}/journee`, { params }),
  /** The officine's treasury settings (P edit, M read) — the comptoir form reads the fonds retenu. */
  parametres: ({ signal } = {}) => api.get("/tresorerie-pharmacie/parametres", signal ? { signal } : {}),

  // ─── TWP-23 — « Paramètres de trésorerie » (the WP-20 workspace section; P edit, M read — TD-152) ─
  /** One PUT of the changed settings (decimal strings, `null` = back to the default) under If-Match. */
  modifierParametres: (body, version) =>
    api.put("/tresorerie-pharmacie/parametres", body, { headers: { "If-Match": `"${version}"` } }),
  /** The motifs admin list (`inclure_inactifs`, `avec_usage` → « Utilisé » + « récurrents actifs »). */
  motifs: (params = {}) => api.get("/tresorerie-pharmacie/motifs", { params }),
  /** A motif of the officine, or a group heading (`est_groupe`). */
  creerMotif: (body) => api.post("/tresorerie-pharmacie/motifs", body),
  /** Rename / re-type / move to another group (If-Match = the motif's version). */
  modifierMotif: (id, body, version) =>
    api.put(`/tresorerie-pharmacie/motifs/${id}`, body, { headers: { "If-Match": `"${version}"` } }),
  /** Deactivate (never delete) or reactivate. */
  changerEtatMotif: (id, is_actif, version) =>
    api.post(`/tresorerie-pharmacie/motifs/${id}/actif`, { is_actif }, version != null ? { headers: { "If-Match": `"${version}"` } } : {}),
  /** The new order of ONE level (a group's motifs, or the root): exactly that level's ids. */
  ordonnerMotifs: (body) => api.put("/tresorerie-pharmacie/motifs/ordre", body),

  // ─── TWP-20 — the one movement form, its confirmation and the operation drawer (decimal strings) ─
  /** The drawer of one operation: header, gestures (`actions`), source document, legs for the owner. */
  operation: (id) => api.get(`/tresorerie-pharmacie/operations/${id}`),
  /** « Historique » (revisions, avant → après) and, for the owner, « Écritures ». */
  historiqueOperation: (id) => api.get(`/tresorerie-pharmacie/operations/${id}/historique`),
  /** The server preview of what is typed (read-only; a creation or `{ operation_id, changements }`). */
  apercuOperation: (body, { signal } = {}) => api.post("/tresorerie-pharmacie/operations/apercu", body, signal ? { signal } : {}),
  /** A manual operation, Brouillon or Confirmé; the form's `idempotency_key` makes a retry a replay. */
  creerOperation: (body) => api.post("/tresorerie-pharmacie/operations", body),
  /** Direct edit (If-Match = the version the form started from; 409 when someone changed it). */
  modifierOperation: (id, body, version) =>
    api.patch(`/tresorerie-pharmacie/operations/${id}`, body, { headers: { "If-Match": `"${version}"` } }),
  /** Confirm one Brouillon (its date de confirmation; If-Match optional). */
  confirmerOperation: (id, body = {}, version = null) =>
    api.post(`/tresorerie-pharmacie/operations/${id}/confirmer`, body, version != null ? { headers: { "If-Match": `"${version}"` } } : {}),
  /** Bulk confirmation, one transaction per operation (TD-126): `{ confirmees, refusees }`. */
  confirmerOperations: (body) => api.post("/tresorerie-pharmacie/operations/confirmer", body),
  /** Cancellation by compensation (motif required; If-Match optional). */
  annulerOperation: (id, motif, version = null) =>
    api.post(`/tresorerie-pharmacie/operations/${id}/annuler`, { motif }, version != null ? { headers: { "If-Match": `"${version}"` } } : {}),

  // ─── TWP-22 — « Ma caisse » (W8): the caller's OPEN comptoir, derived by the server — never an id from here (RC-47) ─
  /** The card: his comptoir, his drawer (null + `masque` when blind), destinations (no balance), motifs, demands, last rows. */
  monComptoir: () => api.get("/tresorerie-pharmacie/comptoirs/moi"),
  /** « Mouvements de ma caisse »: one of HIS sessions (`caisse_id`, default open else last), keyset `apres`, server footer. */
  mouvementsMaCaisse: (params = {}) => api.get("/tresorerie-pharmacie/comptoirs/moi/mouvements", { params }),
  /** The advisory preview of the transfer gesture (refusals in the engine's words, his drawer only). */
  apercuOperationComptoir: (body, { signal } = {}) =>
    api.post("/tresorerie-pharmacie/comptoirs/moi/operations/apercu", body, signal ? { signal } : {}),
  /** The gesture: `{ destination, montant, motif_id, note?, idempotency_key }` — decimal strings, nothing else. */
  creerOperationComptoir: (body) => api.post("/tresorerie-pharmacie/comptoirs/moi/operations", body),
  /** TD-129 — the receiving comptoir acknowledges a transfer « en transit ». */
  accuserReception: (id) => api.post(`/tresorerie-pharmacie/comptoirs/moi/operations/${id}/accuser-reception`, {}),
  /** The author withdraws his own demand still waiting (motif required). */
  retirerDemande: (id, motif) => api.post(`/tresorerie-pharmacie/comptoirs/moi/operations/${id}/annuler`, { motif }),
  /** TD-130 — the 80 mm HTML bon of a counter transfer (printed in the SPA's print frame, no token in a URL). */
  bonTransfertHtml: (id) => api.get(`/print/tresorerie/bon-transfert/${id}`, { responseType: "text" }),

  // ─── TWP-26 — « Règlements » (TWP-24 / TWP-25 server; decimal strings both ways, no-store) ─────────
  /** G1 — the A-02 solde of every fournisseur, the four tiles, the totals of the filtered set (server-paged). */
  soldesFournisseurs: (params = {}, { signal } = {}) => api.get("/tresorerie-pharmacie/reglements/soldes", { params, ...(signal ? { signal } : {}) }),
  /** G2 / T2 — the open factures of one fournisseur (TD-02 order), the A-02 solde, the drafts, the rights. */
  facturesOuvertes: (fournisseurId, params = {}) => api.get(`/tresorerie-pharmacie/reglements/fournisseurs/${fournisseurId}/factures-ouvertes`, { params }),
  /** G2 — the server preview of a settlement (the six §16 figures, source / solde « avant → après », refusals, hash). */
  apercuReglement: (body, { signal } = {}) => api.post("/tresorerie-pharmacie/reglements/ventilation", body, signal ? { signal } : {}),
  /** G2 — the settlement, Brouillon or Confirmé (with its preview hash, TA-17); `idempotency_key` makes a retry a replay. */
  creerReglement: (body) => api.post("/tresorerie-pharmacie/reglements", body),
  /** TD-43 — the preview of « Imputer le crédit » (no source, no mode). */
  apercuImputationCredit: (body, { signal } = {}) => api.post("/tresorerie-pharmacie/reglements/credit/ventilation", body, signal ? { signal } : {}),
  /** TD-43 — impute the supplier credit (zero cash, the preview hash required). */
  imputerCredit: (body) => api.post("/tresorerie-pharmacie/reglements/credit", body),
  /** T2 — the financial profile of a tiers (`:cle` = its id, or `fournisseur:<id>` before its bridge exists). */
  profilTiers: (cle) => api.get(`/tresorerie-pharmacie/tiers/${encodeURIComponent(cle)}/profil`),
  /** T2 « Relevé » — the A-02 relevé of a fournisseur over `[du, au]` (solde cumulé served). */
  releveTiers: (cle, params = {}) => api.get(`/tresorerie-pharmacie/tiers/${encodeURIComponent(cle)}/releve`, { params }),
  /**
   * TWP-28 (TWP-36 W20) — « Paiements enregistrés par vos fournisseurs »: one row per payment a connected
   * fournisseur recorded (`?a_affecter&fournisseur_id&du&au&page&limite`), its état, its draft, the totals.
   */
  paiementsFournisseurs: (params = {}) => api.get("/tresorerie-pharmacie/reglements/paiements-fournisseurs", { params }),
};

// ─── Phase 11 (XpertPharm parity) — Multi-warehouse stock management ──────────
/** Warehouses (magasins) + per-warehouse stock, consolidation, thresholds. */
export const magasinApi = {
  list: (params = {}, config = {}) => api.get("/magasins", { ...config, params }),
  getById: (id) => api.get(`/magasins/${id}`),
  create: (body) => api.post("/magasins", body),
  update: (id, body) => api.put(`/magasins/${id}`, body),
  stock: (id, params = {}) => api.get(`/magasins/${id}/stock`, { params }),
  consolidation: () => api.get("/magasins/consolidation"),
  breaches: (params = {}, config = {}) => api.get("/magasins/breaches", { ...config, params }),
  setSeuil: (id, body) => api.post(`/magasins/${id}/seuils`, body),
  /**
   * X XWP-16 — one level of « Stock global → Magasins → Emplacements → Lots » (02_DESIGN §3.2):
   * no `magasin_id` = level 0, `magasin_id` = level 1, `+ emplacement_id` (uuid · non_range ·
   * tous) = a page of lots; `sort_by` / `sort_order` on levels 1-2, `q` on level 2, `produit_id`.
   */
  hierarchie: (params = {}, config = {}) => api.get("/magasins/hierarchie", { ...config, params }),
};

/** Inter-warehouse stock transfers. */
export const transfertStockApi = {
  /** X XWP-16 — server page: `{ data, total, page, limit, compteurs }` (statut, magasin_id, q, sort). */
  list: (params = {}, config = {}) => api.get("/transferts", { ...config, params }),
  getById: (id, config = {}) => api.get(`/transferts/${id}`, config),
  create: (body) => api.post("/transferts", body),
  validate: (id) => api.post(`/transferts/${id}/validate`),
  cancel: (id) => api.post(`/transferts/${id}/cancel`),
  print: (id) => openPrintDocument(`/transferts/${id}/print`),
};

// ─── Phase 10 (XpertPharm parity) — Financial reports ─────────────────────────
export const rapportFinanceApi = {
  zakat: () => api.get("/rapports/zakat"),
  creancesClients: () => api.get("/rapports/creances-clients"),
  caRecap: (params = {}) => api.get("/rapports/ca-recap", { params }),
  mouvementsTiers: () => api.get("/rapports/mouvements-tiers"),
  transferts: () => api.get("/rapports/transferts"),
};

// ─────────────────────────────────────────────────────────────────────────────
// Export raw API client for custom requests (e.g., nomenclature search)
export { api };
